/**
 * @popperjs/core v2.11.2 - MIT License
 */

(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports) :
  typeof define === 'function' && define.amd ? define(['exports'], factory) :
  (global = typeof globalThis !== 'undefined' ? globalThis : global || self, factory(global.Popper = {}));
}(this, (function (exports) { 'use strict';

  function getWindow(node) {
    if (node == null) {
      return window;
    }

    if (node.toString() !== '[object Window]') {
      var ownerDocument = node.ownerDocument;
      return ownerDocument ? ownerDocument.defaultView || window : window;
    }

    return node;
  }

  function isElement(node) {
    var OwnElement = getWindow(node).Element;
    return node instanceof OwnElement || node instanceof Element;
  }

  function isHTMLElement(node) {
    var OwnElement = getWindow(node).HTMLElement;
    return node instanceof OwnElement || node instanceof HTMLElement;
  }

  function isShadowRoot(node) {
    // IE 11 has no ShadowRoot
    if (typeof ShadowRoot === 'undefined') {
      return false;
    }

    var OwnElement = getWindow(node).ShadowRoot;
    return node instanceof OwnElement || node instanceof ShadowRoot;
  }

  var max = Math.max;
  var min = Math.min;
  var round = Math.round;

  function getBoundingClientRect(element, includeScale) {
    if (includeScale === void 0) {
      includeScale = false;
    }

    var rect = element.getBoundingClientRect();
    var scaleX = 1;
    var scaleY = 1;

    if (isHTMLElement(element) && includeScale) {
      var offsetHeight = element.offsetHeight;
      var offsetWidth = element.offsetWidth; // Do not attempt to divide by 0, otherwise we get `Infinity` as scale
      // Fallback to 1 in case both values are `0`

      if (offsetWidth > 0) {
        scaleX = round(rect.width) / offsetWidth || 1;
      }

      if (offsetHeight > 0) {
        scaleY = round(rect.height) / offsetHeight || 1;
      }
    }

    return {
      width: rect.width / scaleX,
      height: rect.height / scaleY,
      top: rect.top / scaleY,
      right: rect.right / scaleX,
      bottom: rect.bottom / scaleY,
      left: rect.left / scaleX,
      x: rect.left / scaleX,
      y: rect.top / scaleY
    };
  }

  function getWindowScroll(node) {
    var win = getWindow(node);
    var scrollLeft = win.pageXOffset;
    var scrollTop = win.pageYOffset;
    return {
      scrollLeft: scrollLeft,
      scrollTop: scrollTop
    };
  }

  function getHTMLElementScroll(element) {
    return {
      scrollLeft: element.scrollLeft,
      scrollTop: element.scrollTop
    };
  }

  function getNodeScroll(node) {
    if (node === getWindow(node) || !isHTMLElement(node)) {
      return getWindowScroll(node);
    } else {
      return getHTMLElementScroll(node);
    }
  }

  function getNodeName(element) {
    return element ? (element.nodeName || '').toLowerCase() : null;
  }

  function getDocumentElement(element) {
    // $FlowFixMe[incompatible-return]: assume body is always available
    return ((isElement(element) ? element.ownerDocument : // $FlowFixMe[prop-missing]
    element.document) || window.document).documentElement;
  }

  function getWindowScrollBarX(element) {
    // If <html> has a CSS width greater than the viewport, then this will be
    // incorrect for RTL.
    // Popper 1 is broken in this case and never had a bug report so let's assume
    // it's not an issue. I don't think anyone ever specifies width on <html>
    // anyway.
    // Browsers where the left scrollbar doesn't cause an issue report `0` for
    // this (e.g. Edge 2019, IE11, Safari)
    return getBoundingClientRect(getDocumentElement(element)).left + getWindowScroll(element).scrollLeft;
  }

  function getComputedStyle(element) {
    return getWindow(element).getComputedStyle(element);
  }

  function isScrollParent(element) {
    // Firefox wants us to check `-x` and `-y` variations as well
    var _getComputedStyle = getComputedStyle(element),
        overflow = _getComputedStyle.overflow,
        overflowX = _getComputedStyle.overflowX,
        overflowY = _getComputedStyle.overflowY;

    return /auto|scroll|overlay|hidden/.test(overflow + overflowY + overflowX);
  }

  function isElementScaled(element) {
    var rect = element.getBoundingClientRect();
    var scaleX = round(rect.width) / element.offsetWidth || 1;
    var scaleY = round(rect.height) / element.offsetHeight || 1;
    return scaleX !== 1 || scaleY !== 1;
  } // Returns the composite rect of an element relative to its offsetParent.
  // Composite means it takes into account transforms as well as layout.


  function getCompositeRect(elementOrVirtualElement, offsetParent, isFixed) {
    if (isFixed === void 0) {
      isFixed = false;
    }

    var isOffsetParentAnElement = isHTMLElement(offsetParent);
    var offsetParentIsScaled = isHTMLElement(offsetParent) && isElementScaled(offsetParent);
    var documentElement = getDocumentElement(offsetParent);
    var rect = getBoundingClientRect(elementOrVirtualElement, offsetParentIsScaled);
    var scroll = {
      scrollLeft: 0,
      scrollTop: 0
    };
    var offsets = {
      x: 0,
      y: 0
    };

    if (isOffsetParentAnElement || !isOffsetParentAnElement && !isFixed) {
      if (getNodeName(offsetParent) !== 'body' || // https://github.com/popperjs/popper-core/issues/1078
      isScrollParent(documentElement)) {
        scroll = getNodeScroll(offsetParent);
      }

      if (isHTMLElement(offsetParent)) {
        offsets = getBoundingClientRect(offsetParent, true);
        offsets.x += offsetParent.clientLeft;
        offsets.y += offsetParent.clientTop;
      } else if (documentElement) {
        offsets.x = getWindowScrollBarX(documentElement);
      }
    }

    return {
      x: rect.left + scroll.scrollLeft - offsets.x,
      y: rect.top + scroll.scrollTop - offsets.y,
      width: rect.width,
      height: rect.height
    };
  }

  // means it doesn't take into account transforms.

  function getLayoutRect(element) {
    var clientRect = getBoundingClientRect(element); // Use the clientRect sizes if it's not been transformed.
    // Fixes https://github.com/popperjs/popper-core/issues/1223

    var width = element.offsetWidth;
    var height = element.offsetHeight;

    if (Math.abs(clientRect.width - width) <= 1) {
      width = clientRect.width;
    }

    if (Math.abs(clientRect.height - height) <= 1) {
      height = clientRect.height;
    }

    return {
      x: element.offsetLeft,
      y: element.offsetTop,
      width: width,
      height: height
    };
  }

  function getParentNode(element) {
    if (getNodeName(element) === 'html') {
      return element;
    }

    return (// this is a quicker (but less type safe) way to save quite some bytes from the bundle
      // $FlowFixMe[incompatible-return]
      // $FlowFixMe[prop-missing]
      element.assignedSlot || // step into the shadow DOM of the parent of a slotted node
      element.parentNode || ( // DOM Element detected
      isShadowRoot(element) ? element.host : null) || // ShadowRoot detected
      // $FlowFixMe[incompatible-call]: HTMLElement is a Node
      getDocumentElement(element) // fallback

    );
  }

  function getScrollParent(node) {
    if (['html', 'body', '#document'].indexOf(getNodeName(node)) >= 0) {
      // $FlowFixMe[incompatible-return]: assume body is always available
      return node.ownerDocument.body;
    }

    if (isHTMLElement(node) && isScrollParent(node)) {
      return node;
    }

    return getScrollParent(getParentNode(node));
  }

  /*
  given a DOM element, return the list of all scroll parents, up the list of ancesors
  until we get to the top window object. This list is what we attach scroll listeners
  to, because if any of these parent elements scroll, we'll need to re-calculate the
  reference element's position.
  */

  function listScrollParents(element, list) {
    var _element$ownerDocumen;

    if (list === void 0) {
      list = [];
    }

    var scrollParent = getScrollParent(element);
    var isBody = scrollParent === ((_element$ownerDocumen = element.ownerDocument) == null ? void 0 : _element$ownerDocumen.body);
    var win = getWindow(scrollParent);
    var target = isBody ? [win].concat(win.visualViewport || [], isScrollParent(scrollParent) ? scrollParent : []) : scrollParent;
    var updatedList = list.concat(target);
    return isBody ? updatedList : // $FlowFixMe[incompatible-call]: isBody tells us target will be an HTMLElement here
    updatedList.concat(listScrollParents(getParentNode(target)));
  }

  function isTableElement(element) {
    return ['table', 'td', 'th'].indexOf(getNodeName(element)) >= 0;
  }

  function getTrueOffsetParent(element) {
    if (!isHTMLElement(element) || // https://github.com/popperjs/popper-core/issues/837
    getComputedStyle(element).position === 'fixed') {
      return null;
    }

    return element.offsetParent;
  } // `.offsetParent` reports `null` for fixed elements, while absolute elements
  // return the containing block


  function getContainingBlock(element) {
    var isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') !== -1;
    var isIE = navigator.userAgent.indexOf('Trident') !== -1;

    if (isIE && isHTMLElement(element)) {
      // In IE 9, 10 and 11 fixed elements containing block is always established by the viewport
      var elementCss = getComputedStyle(element);

      if (elementCss.position === 'fixed') {
        return null;
      }
    }

    var currentNode = getParentNode(element);

    while (isHTMLElement(currentNode) && ['html', 'body'].indexOf(getNodeName(currentNode)) < 0) {
      var css = getComputedStyle(currentNode); // This is non-exhaustive but covers the most common CSS properties that
      // create a containing block.
      // https://developer.mozilla.org/en-US/docs/Web/CSS/Containing_block#identifying_the_containing_block

      if (css.transform !== 'none' || css.perspective !== 'none' || css.contain === 'paint' || ['transform', 'perspective'].indexOf(css.willChange) !== -1 || isFirefox && css.willChange === 'filter' || isFirefox && css.filter && css.filter !== 'none') {
        return currentNode;
      } else {
        currentNode = currentNode.parentNode;
      }
    }

    return null;
  } // Gets the closest ancestor positioned element. Handles some edge cases,
  // such as table ancestors and cross browser bugs.


  function getOffsetParent(element) {
    var window = getWindow(element);
    var offsetParent = getTrueOffsetParent(element);

    while (offsetParent && isTableElement(offsetParent) && getComputedStyle(offsetParent).position === 'static') {
      offsetParent = getTrueOffsetParent(offsetParent);
    }

    if (offsetParent && (getNodeName(offsetParent) === 'html' || getNodeName(offsetParent) === 'body' && getComputedStyle(offsetParent).position === 'static')) {
      return window;
    }

    return offsetParent || getContainingBlock(element) || window;
  }

  var top = 'top';
  var bottom = 'bottom';
  var right = 'right';
  var left = 'left';
  var auto = 'auto';
  var basePlacements = [top, bottom, right, left];
  var start = 'start';
  var end = 'end';
  var clippingParents = 'clippingParents';
  var viewport = 'viewport';
  var popper = 'popper';
  var reference = 'reference';
  var variationPlacements = /*#__PURE__*/basePlacements.reduce(function (acc, placement) {
    return acc.concat([placement + "-" + start, placement + "-" + end]);
  }, []);
  var placements = /*#__PURE__*/[].concat(basePlacements, [auto]).reduce(function (acc, placement) {
    return acc.concat([placement, placement + "-" + start, placement + "-" + end]);
  }, []); // modifiers that need to read the DOM

  var beforeRead = 'beforeRead';
  var read = 'read';
  var afterRead = 'afterRead'; // pure-logic modifiers

  var beforeMain = 'beforeMain';
  var main = 'main';
  var afterMain = 'afterMain'; // modifier with the purpose to write to the DOM (or write into a framework state)

  var beforeWrite = 'beforeWrite';
  var write = 'write';
  var afterWrite = 'afterWrite';
  var modifierPhases = [beforeRead, read, afterRead, beforeMain, main, afterMain, beforeWrite, write, afterWrite];

  function order(modifiers) {
    var map = new Map();
    var visited = new Set();
    var result = [];
    modifiers.forEach(function (modifier) {
      map.set(modifier.name, modifier);
    }); // On visiting object, check for its dependencies and visit them recursively

    function sort(modifier) {
      visited.add(modifier.name);
      var requires = [].concat(modifier.requires || [], modifier.requiresIfExists || []);
      requires.forEach(function (dep) {
        if (!visited.has(dep)) {
          var depModifier = map.get(dep);

          if (depModifier) {
            sort(depModifier);
          }
        }
      });
      result.push(modifier);
    }

    modifiers.forEach(function (modifier) {
      if (!visited.has(modifier.name)) {
        // check for visited object
        sort(modifier);
      }
    });
    return result;
  }

  function orderModifiers(modifiers) {
    // order based on dependencies
    var orderedModifiers = order(modifiers); // order based on phase

    return modifierPhases.reduce(function (acc, phase) {
      return acc.concat(orderedModifiers.filter(function (modifier) {
        return modifier.phase === phase;
      }));
    }, []);
  }

  function debounce(fn) {
    var pending;
    return function () {
      if (!pending) {
        pending = new Promise(function (resolve) {
          Promise.resolve().then(function () {
            pending = undefined;
            resolve(fn());
          });
        });
      }

      return pending;
    };
  }

  function format(str) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }

    return [].concat(args).reduce(function (p, c) {
      return p.replace(/%s/, c);
    }, str);
  }

  var INVALID_MODIFIER_ERROR = 'Popper: modifier "%s" provided an invalid %s property, expected %s but got %s';
  var MISSING_DEPENDENCY_ERROR = 'Popper: modifier "%s" requires "%s", but "%s" modifier is not available';
  var VALID_PROPERTIES = ['name', 'enabled', 'phase', 'fn', 'effect', 'requires', 'options'];
  function validateModifiers(modifiers) {
    modifiers.forEach(function (modifier) {
      [].concat(Object.keys(modifier), VALID_PROPERTIES) // IE11-compatible replacement for `new Set(iterable)`
      .filter(function (value, index, self) {
        return self.indexOf(value) === index;
      }).forEach(function (key) {
        switch (key) {
          case 'name':
            if (typeof modifier.name !== 'string') {
              console.error(format(INVALID_MODIFIER_ERROR, String(modifier.name), '"name"', '"string"', "\"" + String(modifier.name) + "\""));
            }

            break;

          case 'enabled':
            if (typeof modifier.enabled !== 'boolean') {
              console.error(format(INVALID_MODIFIER_ERROR, modifier.name, '"enabled"', '"boolean"', "\"" + String(modifier.enabled) + "\""));
            }

            break;

          case 'phase':
            if (modifierPhases.indexOf(modifier.phase) < 0) {
              console.error(format(INVALID_MODIFIER_ERROR, modifier.name, '"phase"', "either " + modifierPhases.join(', '), "\"" + String(modifier.phase) + "\""));
            }

            break;

          case 'fn':
            if (typeof modifier.fn !== 'function') {
              console.error(format(INVALID_MODIFIER_ERROR, modifier.name, '"fn"', '"function"', "\"" + String(modifier.fn) + "\""));
            }

            break;

          case 'effect':
            if (modifier.effect != null && typeof modifier.effect !== 'function') {
              console.error(format(INVALID_MODIFIER_ERROR, modifier.name, '"effect"', '"function"', "\"" + String(modifier.fn) + "\""));
            }

            break;

          case 'requires':
            if (modifier.requires != null && !Array.isArray(modifier.requires)) {
              console.error(format(INVALID_MODIFIER_ERROR, modifier.name, '"requires"', '"array"', "\"" + String(modifier.requires) + "\""));
            }

            break;

          case 'requiresIfExists':
            if (!Array.isArray(modifier.requiresIfExists)) {
              console.error(format(INVALID_MODIFIER_ERROR, modifier.name, '"requiresIfExists"', '"array"', "\"" + String(modifier.requiresIfExists) + "\""));
            }

            break;

          case 'options':
          case 'data':
            break;

          default:
            console.error("PopperJS: an invalid property has been provided to the \"" + modifier.name + "\" modifier, valid properties are " + VALID_PROPERTIES.map(function (s) {
              return "\"" + s + "\"";
            }).join(', ') + "; but \"" + key + "\" was provided.");
        }

        modifier.requires && modifier.requires.forEach(function (requirement) {
          if (modifiers.find(function (mod) {
            return mod.name === requirement;
          }) == null) {
            console.error(format(MISSING_DEPENDENCY_ERROR, String(modifier.name), requirement, requirement));
          }
        });
      });
    });
  }

  function uniqueBy(arr, fn) {
    var identifiers = new Set();
    return arr.filter(function (item) {
      var identifier = fn(item);

      if (!identifiers.has(identifier)) {
        identifiers.add(identifier);
        return true;
      }
    });
  }

  function getBasePlacement(placement) {
    return placement.split('-')[0];
  }

  function mergeByName(modifiers) {
    var merged = modifiers.reduce(function (merged, current) {
      var existing = merged[current.name];
      merged[current.name] = existing ? Object.assign({}, existing, current, {
        options: Object.assign({}, existing.options, current.options),
        data: Object.assign({}, existing.data, current.data)
      }) : current;
      return merged;
    }, {}); // IE11 does not support Object.values

    return Object.keys(merged).map(function (key) {
      return merged[key];
    });
  }

  function getViewportRect(element) {
    var win = getWindow(element);
    var html = getDocumentElement(element);
    var visualViewport = win.visualViewport;
    var width = html.clientWidth;
    var height = html.clientHeight;
    var x = 0;
    var y = 0; // NB: This isn't supported on iOS <= 12. If the keyboard is open, the popper
    // can be obscured underneath it.
    // Also, `html.clientHeight` adds the bottom bar height in Safari iOS, even
    // if it isn't open, so if this isn't available, the popper will be detected
    // to overflow the bottom of the screen too early.

    if (visualViewport) {
      width = visualViewport.width;
      height = visualViewport.height; // Uses Layout Viewport (like Chrome; Safari does not currently)
      // In Chrome, it returns a value very close to 0 (+/-) but contains rounding
      // errors due to floating point numbers, so we need to check precision.
      // Safari returns a number <= 0, usually < -1 when pinch-zoomed
      // Feature detection fails in mobile emulation mode in Chrome.
      // Math.abs(win.innerWidth / visualViewport.scale - visualViewport.width) <
      // 0.001
      // Fallback here: "Not Safari" userAgent

      if (!/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
        x = visualViewport.offsetLeft;
        y = visualViewport.offsetTop;
      }
    }

    return {
      width: width,
      height: height,
      x: x + getWindowScrollBarX(element),
      y: y
    };
  }

  // of the `<html>` and `<body>` rect bounds if horizontally scrollable

  function getDocumentRect(element) {
    var _element$ownerDocumen;

    var html = getDocumentElement(element);
    var winScroll = getWindowScroll(element);
    var body = (_element$ownerDocumen = element.ownerDocument) == null ? void 0 : _element$ownerDocumen.body;
    var width = max(html.scrollWidth, html.clientWidth, body ? body.scrollWidth : 0, body ? body.clientWidth : 0);
    var height = max(html.scrollHeight, html.clientHeight, body ? body.scrollHeight : 0, body ? body.clientHeight : 0);
    var x = -winScroll.scrollLeft + getWindowScrollBarX(element);
    var y = -winScroll.scrollTop;

    if (getComputedStyle(body || html).direction === 'rtl') {
      x += max(html.clientWidth, body ? body.clientWidth : 0) - width;
    }

    return {
      width: width,
      height: height,
      x: x,
      y: y
    };
  }

  function contains(parent, child) {
    var rootNode = child.getRootNode && child.getRootNode(); // First, attempt with faster native method

    if (parent.contains(child)) {
      return true;
    } // then fallback to custom implementation with Shadow DOM support
    else if (rootNode && isShadowRoot(rootNode)) {
        var next = child;

        do {
          if (next && parent.isSameNode(next)) {
            return true;
          } // $FlowFixMe[prop-missing]: need a better way to handle this...


          next = next.parentNode || next.host;
        } while (next);
      } // Give up, the result is false


    return false;
  }

  function rectToClientRect(rect) {
    return Object.assign({}, rect, {
      left: rect.x,
      top: rect.y,
      right: rect.x + rect.width,
      bottom: rect.y + rect.height
    });
  }

  function getInnerBoundingClientRect(element) {
    var rect = getBoundingClientRect(element);
    rect.top = rect.top + element.clientTop;
    rect.left = rect.left + element.clientLeft;
    rect.bottom = rect.top + element.clientHeight;
    rect.right = rect.left + element.clientWidth;
    rect.width = element.clientWidth;
    rect.height = element.clientHeight;
    rect.x = rect.left;
    rect.y = rect.top;
    return rect;
  }

  function getClientRectFromMixedType(element, clippingParent) {
    return clippingParent === viewport ? rectToClientRect(getViewportRect(element)) : isElement(clippingParent) ? getInnerBoundingClientRect(clippingParent) : rectToClientRect(getDocumentRect(getDocumentElement(element)));
  } // A "clipping parent" is an overflowable container with the characteristic of
  // clipping (or hiding) overflowing elements with a position different from
  // `initial`


  function getClippingParents(element) {
    var clippingParents = listScrollParents(getParentNode(element));
    var canEscapeClipping = ['absolute', 'fixed'].indexOf(getComputedStyle(element).position) >= 0;
    var clipperElement = canEscapeClipping && isHTMLElement(element) ? getOffsetParent(element) : element;

    if (!isElement(clipperElement)) {
      return [];
    } // $FlowFixMe[incompatible-return]: https://github.com/facebook/flow/issues/1414


    return clippingParents.filter(function (clippingParent) {
      return isElement(clippingParent) && contains(clippingParent, clipperElement) && getNodeName(clippingParent) !== 'body';
    });
  } // Gets the maximum area that the element is visible in due to any number of
  // clipping parents


  function getClippingRect(element, boundary, rootBoundary) {
    var mainClippingParents = boundary === 'clippingParents' ? getClippingParents(element) : [].concat(boundary);
    var clippingParents = [].concat(mainClippingParents, [rootBoundary]);
    var firstClippingParent = clippingParents[0];
    var clippingRect = clippingParents.reduce(function (accRect, clippingParent) {
      var rect = getClientRectFromMixedType(element, clippingParent);
      accRect.top = max(rect.top, accRect.top);
      accRect.right = min(rect.right, accRect.right);
      accRect.bottom = min(rect.bottom, accRect.bottom);
      accRect.left = max(rect.left, accRect.left);
      return accRect;
    }, getClientRectFromMixedType(element, firstClippingParent));
    clippingRect.width = clippingRect.right - clippingRect.left;
    clippingRect.height = clippingRect.bottom - clippingRect.top;
    clippingRect.x = clippingRect.left;
    clippingRect.y = clippingRect.top;
    return clippingRect;
  }

  function getVariation(placement) {
    return placement.split('-')[1];
  }

  function getMainAxisFromPlacement(placement) {
    return ['top', 'bottom'].indexOf(placement) >= 0 ? 'x' : 'y';
  }

  function computeOffsets(_ref) {
    var reference = _ref.reference,
        element = _ref.element,
        placement = _ref.placement;
    var basePlacement = placement ? getBasePlacement(placement) : null;
    var variation = placement ? getVariation(placement) : null;
    var commonX = reference.x + reference.width / 2 - element.width / 2;
    var commonY = reference.y + reference.height / 2 - element.height / 2;
    var offsets;

    switch (basePlacement) {
      case top:
        offsets = {
          x: commonX,
          y: reference.y - element.height
        };
        break;

      case bottom:
        offsets = {
          x: commonX,
          y: reference.y + reference.height
        };
        break;

      case right:
        offsets = {
          x: reference.x + reference.width,
          y: commonY
        };
        break;

      case left:
        offsets = {
          x: reference.x - element.width,
          y: commonY
        };
        break;

      default:
        offsets = {
          x: reference.x,
          y: reference.y
        };
    }

    var mainAxis = basePlacement ? getMainAxisFromPlacement(basePlacement) : null;

    if (mainAxis != null) {
      var len = mainAxis === 'y' ? 'height' : 'width';

      switch (variation) {
        case start:
          offsets[mainAxis] = offsets[mainAxis] - (reference[len] / 2 - element[len] / 2);
          break;

        case end:
          offsets[mainAxis] = offsets[mainAxis] + (reference[len] / 2 - element[len] / 2);
          break;
      }
    }

    return offsets;
  }

  function getFreshSideObject() {
    return {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    };
  }

  function mergePaddingObject(paddingObject) {
    return Object.assign({}, getFreshSideObject(), paddingObject);
  }

  function expandToHashMap(value, keys) {
    return keys.reduce(function (hashMap, key) {
      hashMap[key] = value;
      return hashMap;
    }, {});
  }

  function detectOverflow(state, options) {
    if (options === void 0) {
      options = {};
    }

    var _options = options,
        _options$placement = _options.placement,
        placement = _options$placement === void 0 ? state.placement : _options$placement,
        _options$boundary = _options.boundary,
        boundary = _options$boundary === void 0 ? clippingParents : _options$boundary,
        _options$rootBoundary = _options.rootBoundary,
        rootBoundary = _options$rootBoundary === void 0 ? viewport : _options$rootBoundary,
        _options$elementConte = _options.elementContext,
        elementContext = _options$elementConte === void 0 ? popper : _options$elementConte,
        _options$altBoundary = _options.altBoundary,
        altBoundary = _options$altBoundary === void 0 ? false : _options$altBoundary,
        _options$padding = _options.padding,
        padding = _options$padding === void 0 ? 0 : _options$padding;
    var paddingObject = mergePaddingObject(typeof padding !== 'number' ? padding : expandToHashMap(padding, basePlacements));
    var altContext = elementContext === popper ? reference : popper;
    var popperRect = state.rects.popper;
    var element = state.elements[altBoundary ? altContext : elementContext];
    var clippingClientRect = getClippingRect(isElement(element) ? element : element.contextElement || getDocumentElement(state.elements.popper), boundary, rootBoundary);
    var referenceClientRect = getBoundingClientRect(state.elements.reference);
    var popperOffsets = computeOffsets({
      reference: referenceClientRect,
      element: popperRect,
      strategy: 'absolute',
      placement: placement
    });
    var popperClientRect = rectToClientRect(Object.assign({}, popperRect, popperOffsets));
    var elementClientRect = elementContext === popper ? popperClientRect : referenceClientRect; // positive = overflowing the clipping rect
    // 0 or negative = within the clipping rect

    var overflowOffsets = {
      top: clippingClientRect.top - elementClientRect.top + paddingObject.top,
      bottom: elementClientRect.bottom - clippingClientRect.bottom + paddingObject.bottom,
      left: clippingClientRect.left - elementClientRect.left + paddingObject.left,
      right: elementClientRect.right - clippingClientRect.right + paddingObject.right
    };
    var offsetData = state.modifiersData.offset; // Offsets can be applied only to the popper element

    if (elementContext === popper && offsetData) {
      var offset = offsetData[placement];
      Object.keys(overflowOffsets).forEach(function (key) {
        var multiply = [right, bottom].indexOf(key) >= 0 ? 1 : -1;
        var axis = [top, bottom].indexOf(key) >= 0 ? 'y' : 'x';
        overflowOffsets[key] += offset[axis] * multiply;
      });
    }

    return overflowOffsets;
  }

  var INVALID_ELEMENT_ERROR = 'Popper: Invalid reference or popper argument provided. They must be either a DOM element or virtual element.';
  var INFINITE_LOOP_ERROR = 'Popper: An infinite loop in the modifiers cycle has been detected! The cycle has been interrupted to prevent a browser crash.';
  var DEFAULT_OPTIONS = {
    placement: 'bottom',
    modifiers: [],
    strategy: 'absolute'
  };

  function areValidElements() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return !args.some(function (element) {
      return !(element && typeof element.getBoundingClientRect === 'function');
    });
  }

  function popperGenerator(generatorOptions) {
    if (generatorOptions === void 0) {
      generatorOptions = {};
    }

    var _generatorOptions = generatorOptions,
        _generatorOptions$def = _generatorOptions.defaultModifiers,
        defaultModifiers = _generatorOptions$def === void 0 ? [] : _generatorOptions$def,
        _generatorOptions$def2 = _generatorOptions.defaultOptions,
        defaultOptions = _generatorOptions$def2 === void 0 ? DEFAULT_OPTIONS : _generatorOptions$def2;
    return function createPopper(reference, popper, options) {
      if (options === void 0) {
        options = defaultOptions;
      }

      var state = {
        placement: 'bottom',
        orderedModifiers: [],
        options: Object.assign({}, DEFAULT_OPTIONS, defaultOptions),
        modifiersData: {},
        elements: {
          reference: reference,
          popper: popper
        },
        attributes: {},
        styles: {}
      };
      var effectCleanupFns = [];
      var isDestroyed = false;
      var instance = {
        state: state,
        setOptions: function setOptions(setOptionsAction) {
          var options = typeof setOptionsAction === 'function' ? setOptionsAction(state.options) : setOptionsAction;
          cleanupModifierEffects();
          state.options = Object.assign({}, defaultOptions, state.options, options);
          state.scrollParents = {
            reference: isElement(reference) ? listScrollParents(reference) : reference.contextElement ? listScrollParents(reference.contextElement) : [],
            popper: listScrollParents(popper)
          }; // Orders the modifiers based on their dependencies and `phase`
          // properties

          var orderedModifiers = orderModifiers(mergeByName([].concat(defaultModifiers, state.options.modifiers))); // Strip out disabled modifiers

          state.orderedModifiers = orderedModifiers.filter(function (m) {
            return m.enabled;
          }); // Validate the provided modifiers so that the consumer will get warned
          // if one of the modifiers is invalid for any reason

          {
            var modifiers = uniqueBy([].concat(orderedModifiers, state.options.modifiers), function (_ref) {
              var name = _ref.name;
              return name;
            });
            validateModifiers(modifiers);

            if (getBasePlacement(state.options.placement) === auto) {
              var flipModifier = state.orderedModifiers.find(function (_ref2) {
                var name = _ref2.name;
                return name === 'flip';
              });

              if (!flipModifier) {
                console.error(['Popper: "auto" placements require the "flip" modifier be', 'present and enabled to work.'].join(' '));
              }
            }

            var _getComputedStyle = getComputedStyle(popper),
                marginTop = _getComputedStyle.marginTop,
                marginRight = _getComputedStyle.marginRight,
                marginBottom = _getComputedStyle.marginBottom,
                marginLeft = _getComputedStyle.marginLeft; // We no longer take into account `margins` on the popper, and it can
            // cause bugs with positioning, so we'll warn the consumer


            if ([marginTop, marginRight, marginBottom, marginLeft].some(function (margin) {
              return parseFloat(margin);
            })) {
              console.warn(['Popper: CSS "margin" styles cannot be used to apply padding', 'between the popper and its reference element or boundary.', 'To replicate margin, use the `offset` modifier, as well as', 'the `padding` option in the `preventOverflow` and `flip`', 'modifiers.'].join(' '));
            }
          }

          runModifierEffects();
          return instance.update();
        },
        // Sync update – it will always be executed, even if not necessary. This
        // is useful for low frequency updates where sync behavior simplifies the
        // logic.
        // For high frequency updates (e.g. `resize` and `scroll` events), always
        // prefer the async Popper#update method
        forceUpdate: function forceUpdate() {
          if (isDestroyed) {
            return;
          }

          var _state$elements = state.elements,
              reference = _state$elements.reference,
              popper = _state$elements.popper; // Don't proceed if `reference` or `popper` are not valid elements
          // anymore

          if (!areValidElements(reference, popper)) {
            {
              console.error(INVALID_ELEMENT_ERROR);
            }

            return;
          } // Store the reference and popper rects to be read by modifiers


          state.rects = {
            reference: getCompositeRect(reference, getOffsetParent(popper), state.options.strategy === 'fixed'),
            popper: getLayoutRect(popper)
          }; // Modifiers have the ability to reset the current update cycle. The
          // most common use case for this is the `flip` modifier changing the
          // placement, which then needs to re-run all the modifiers, because the
          // logic was previously ran for the previous placement and is therefore
          // stale/incorrect

          state.reset = false;
          state.placement = state.options.placement; // On each update cycle, the `modifiersData` property for each modifier
          // is filled with the initial data specified by the modifier. This means
          // it doesn't persist and is fresh on each update.
          // To ensure persistent data, use `${name}#persistent`

          state.orderedModifiers.forEach(function (modifier) {
            return state.modifiersData[modifier.name] = Object.assign({}, modifier.data);
          });
          var __debug_loops__ = 0;

          for (var index = 0; index < state.orderedModifiers.length; index++) {
            {
              __debug_loops__ += 1;

              if (__debug_loops__ > 100) {
                console.error(INFINITE_LOOP_ERROR);
                break;
              }
            }

            if (state.reset === true) {
              state.reset = false;
              index = -1;
              continue;
            }

            var _state$orderedModifie = state.orderedModifiers[index],
                fn = _state$orderedModifie.fn,
                _state$orderedModifie2 = _state$orderedModifie.options,
                _options = _state$orderedModifie2 === void 0 ? {} : _state$orderedModifie2,
                name = _state$orderedModifie.name;

            if (typeof fn === 'function') {
              state = fn({
                state: state,
                options: _options,
                name: name,
                instance: instance
              }) || state;
            }
          }
        },
        // Async and optimistically optimized update – it will not be executed if
        // not necessary (debounced to run at most once-per-tick)
        update: debounce(function () {
          return new Promise(function (resolve) {
            instance.forceUpdate();
            resolve(state);
          });
        }),
        destroy: function destroy() {
          cleanupModifierEffects();
          isDestroyed = true;
        }
      };

      if (!areValidElements(reference, popper)) {
        {
          console.error(INVALID_ELEMENT_ERROR);
        }

        return instance;
      }

      instance.setOptions(options).then(function (state) {
        if (!isDestroyed && options.onFirstUpdate) {
          options.onFirstUpdate(state);
        }
      }); // Modifiers have the ability to execute arbitrary code before the first
      // update cycle runs. They will be executed in the same order as the update
      // cycle. This is useful when a modifier adds some persistent data that
      // other modifiers need to use, but the modifier is run after the dependent
      // one.

      function runModifierEffects() {
        state.orderedModifiers.forEach(function (_ref3) {
          var name = _ref3.name,
              _ref3$options = _ref3.options,
              options = _ref3$options === void 0 ? {} : _ref3$options,
              effect = _ref3.effect;

          if (typeof effect === 'function') {
            var cleanupFn = effect({
              state: state,
              name: name,
              instance: instance,
              options: options
            });

            var noopFn = function noopFn() {};

            effectCleanupFns.push(cleanupFn || noopFn);
          }
        });
      }

      function cleanupModifierEffects() {
        effectCleanupFns.forEach(function (fn) {
          return fn();
        });
        effectCleanupFns = [];
      }

      return instance;
    };
  }

  var passive = {
    passive: true
  };

  function effect$2(_ref) {
    var state = _ref.state,
        instance = _ref.instance,
        options = _ref.options;
    var _options$scroll = options.scroll,
        scroll = _options$scroll === void 0 ? true : _options$scroll,
        _options$resize = options.resize,
        resize = _options$resize === void 0 ? true : _options$resize;
    var window = getWindow(state.elements.popper);
    var scrollParents = [].concat(state.scrollParents.reference, state.scrollParents.popper);

    if (scroll) {
      scrollParents.forEach(function (scrollParent) {
        scrollParent.addEventListener('scroll', instance.update, passive);
      });
    }

    if (resize) {
      window.addEventListener('resize', instance.update, passive);
    }

    return function () {
      if (scroll) {
        scrollParents.forEach(function (scrollParent) {
          scrollParent.removeEventListener('scroll', instance.update, passive);
        });
      }

      if (resize) {
        window.removeEventListener('resize', instance.update, passive);
      }
    };
  } // eslint-disable-next-line import/no-unused-modules


  var eventListeners = {
    name: 'eventListeners',
    enabled: true,
    phase: 'write',
    fn: function fn() {},
    effect: effect$2,
    data: {}
  };

  function popperOffsets(_ref) {
    var state = _ref.state,
        name = _ref.name;
    // Offsets are the actual position the popper needs to have to be
    // properly positioned near its reference element
    // This is the most basic placement, and will be adjusted by
    // the modifiers in the next step
    state.modifiersData[name] = computeOffsets({
      reference: state.rects.reference,
      element: state.rects.popper,
      strategy: 'absolute',
      placement: state.placement
    });
  } // eslint-disable-next-line import/no-unused-modules


  var popperOffsets$1 = {
    name: 'popperOffsets',
    enabled: true,
    phase: 'read',
    fn: popperOffsets,
    data: {}
  };

  var unsetSides = {
    top: 'auto',
    right: 'auto',
    bottom: 'auto',
    left: 'auto'
  }; // Round the offsets to the nearest suitable subpixel based on the DPR.
  // Zooming can change the DPR, but it seems to report a value that will
  // cleanly divide the values into the appropriate subpixels.

  function roundOffsetsByDPR(_ref) {
    var x = _ref.x,
        y = _ref.y;
    var win = window;
    var dpr = win.devicePixelRatio || 1;
    return {
      x: round(x * dpr) / dpr || 0,
      y: round(y * dpr) / dpr || 0
    };
  }

  function mapToStyles(_ref2) {
    var _Object$assign2;

    var popper = _ref2.popper,
        popperRect = _ref2.popperRect,
        placement = _ref2.placement,
        variation = _ref2.variation,
        offsets = _ref2.offsets,
        position = _ref2.position,
        gpuAcceleration = _ref2.gpuAcceleration,
        adaptive = _ref2.adaptive,
        roundOffsets = _ref2.roundOffsets,
        isFixed = _ref2.isFixed;
    var _offsets$x = offsets.x,
        x = _offsets$x === void 0 ? 0 : _offsets$x,
        _offsets$y = offsets.y,
        y = _offsets$y === void 0 ? 0 : _offsets$y;

    var _ref3 = typeof roundOffsets === 'function' ? roundOffsets({
      x: x,
      y: y
    }) : {
      x: x,
      y: y
    };

    x = _ref3.x;
    y = _ref3.y;
    var hasX = offsets.hasOwnProperty('x');
    var hasY = offsets.hasOwnProperty('y');
    var sideX = left;
    var sideY = top;
    var win = window;

    if (adaptive) {
      var offsetParent = getOffsetParent(popper);
      var heightProp = 'clientHeight';
      var widthProp = 'clientWidth';

      if (offsetParent === getWindow(popper)) {
        offsetParent = getDocumentElement(popper);

        if (getComputedStyle(offsetParent).position !== 'static' && position === 'absolute') {
          heightProp = 'scrollHeight';
          widthProp = 'scrollWidth';
        }
      } // $FlowFixMe[incompatible-cast]: force type refinement, we compare offsetParent with window above, but Flow doesn't detect it


      offsetParent = offsetParent;

      if (placement === top || (placement === left || placement === right) && variation === end) {
        sideY = bottom;
        var offsetY = isFixed && win.visualViewport ? win.visualViewport.height : // $FlowFixMe[prop-missing]
        offsetParent[heightProp];
        y -= offsetY - popperRect.height;
        y *= gpuAcceleration ? 1 : -1;
      }

      if (placement === left || (placement === top || placement === bottom) && variation === end) {
        sideX = right;
        var offsetX = isFixed && win.visualViewport ? win.visualViewport.width : // $FlowFixMe[prop-missing]
        offsetParent[widthProp];
        x -= offsetX - popperRect.width;
        x *= gpuAcceleration ? 1 : -1;
      }
    }

    var commonStyles = Object.assign({
      position: position
    }, adaptive && unsetSides);

    var _ref4 = roundOffsets === true ? roundOffsetsByDPR({
      x: x,
      y: y
    }) : {
      x: x,
      y: y
    };

    x = _ref4.x;
    y = _ref4.y;

    if (gpuAcceleration) {
      var _Object$assign;

      return Object.assign({}, commonStyles, (_Object$assign = {}, _Object$assign[sideY] = hasY ? '0' : '', _Object$assign[sideX] = hasX ? '0' : '', _Object$assign.transform = (win.devicePixelRatio || 1) <= 1 ? "translate(" + x + "px, " + y + "px)" : "translate3d(" + x + "px, " + y + "px, 0)", _Object$assign));
    }

    return Object.assign({}, commonStyles, (_Object$assign2 = {}, _Object$assign2[sideY] = hasY ? y + "px" : '', _Object$assign2[sideX] = hasX ? x + "px" : '', _Object$assign2.transform = '', _Object$assign2));
  }

  function computeStyles(_ref5) {
    var state = _ref5.state,
        options = _ref5.options;
    var _options$gpuAccelerat = options.gpuAcceleration,
        gpuAcceleration = _options$gpuAccelerat === void 0 ? true : _options$gpuAccelerat,
        _options$adaptive = options.adaptive,
        adaptive = _options$adaptive === void 0 ? true : _options$adaptive,
        _options$roundOffsets = options.roundOffsets,
        roundOffsets = _options$roundOffsets === void 0 ? true : _options$roundOffsets;

    {
      var transitionProperty = getComputedStyle(state.elements.popper).transitionProperty || '';

      if (adaptive && ['transform', 'top', 'right', 'bottom', 'left'].some(function (property) {
        return transitionProperty.indexOf(property) >= 0;
      })) {
        console.warn(['Popper: Detected CSS transitions on at least one of the following', 'CSS properties: "transform", "top", "right", "bottom", "left".', '\n\n', 'Disable the "computeStyles" modifier\'s `adaptive` option to allow', 'for smooth transitions, or remove these properties from the CSS', 'transition declaration on the popper element if only transitioning', 'opacity or background-color for example.', '\n\n', 'We recommend using the popper element as a wrapper around an inner', 'element that can have any CSS property transitioned for animations.'].join(' '));
      }
    }

    var commonStyles = {
      placement: getBasePlacement(state.placement),
      variation: getVariation(state.placement),
      popper: state.elements.popper,
      popperRect: state.rects.popper,
      gpuAcceleration: gpuAcceleration,
      isFixed: state.options.strategy === 'fixed'
    };

    if (state.modifiersData.popperOffsets != null) {
      state.styles.popper = Object.assign({}, state.styles.popper, mapToStyles(Object.assign({}, commonStyles, {
        offsets: state.modifiersData.popperOffsets,
        position: state.options.strategy,
        adaptive: adaptive,
        roundOffsets: roundOffsets
      })));
    }

    if (state.modifiersData.arrow != null) {
      state.styles.arrow = Object.assign({}, state.styles.arrow, mapToStyles(Object.assign({}, commonStyles, {
        offsets: state.modifiersData.arrow,
        position: 'absolute',
        adaptive: false,
        roundOffsets: roundOffsets
      })));
    }

    state.attributes.popper = Object.assign({}, state.attributes.popper, {
      'data-popper-placement': state.placement
    });
  } // eslint-disable-next-line import/no-unused-modules


  var computeStyles$1 = {
    name: 'computeStyles',
    enabled: true,
    phase: 'beforeWrite',
    fn: computeStyles,
    data: {}
  };

  // and applies them to the HTMLElements such as popper and arrow

  function applyStyles(_ref) {
    var state = _ref.state;
    Object.keys(state.elements).forEach(function (name) {
      var style = state.styles[name] || {};
      var attributes = state.attributes[name] || {};
      var element = state.elements[name]; // arrow is optional + virtual elements

      if (!isHTMLElement(element) || !getNodeName(element)) {
        return;
      } // Flow doesn't support to extend this property, but it's the most
      // effective way to apply styles to an HTMLElement
      // $FlowFixMe[cannot-write]


      Object.assign(element.style, style);
      Object.keys(attributes).forEach(function (name) {
        var value = attributes[name];

        if (value === false) {
          element.removeAttribute(name);
        } else {
          element.setAttribute(name, value === true ? '' : value);
        }
      });
    });
  }

  function effect$1(_ref2) {
    var state = _ref2.state;
    var initialStyles = {
      popper: {
        position: state.options.strategy,
        left: '0',
        top: '0',
        margin: '0'
      },
      arrow: {
        position: 'absolute'
      },
      reference: {}
    };
    Object.assign(state.elements.popper.style, initialStyles.popper);
    state.styles = initialStyles;

    if (state.elements.arrow) {
      Object.assign(state.elements.arrow.style, initialStyles.arrow);
    }

    return function () {
      Object.keys(state.elements).forEach(function (name) {
        var element = state.elements[name];
        var attributes = state.attributes[name] || {};
        var styleProperties = Object.keys(state.styles.hasOwnProperty(name) ? state.styles[name] : initialStyles[name]); // Set all values to an empty string to unset them

        var style = styleProperties.reduce(function (style, property) {
          style[property] = '';
          return style;
        }, {}); // arrow is optional + virtual elements

        if (!isHTMLElement(element) || !getNodeName(element)) {
          return;
        }

        Object.assign(element.style, style);
        Object.keys(attributes).forEach(function (attribute) {
          element.removeAttribute(attribute);
        });
      });
    };
  } // eslint-disable-next-line import/no-unused-modules


  var applyStyles$1 = {
    name: 'applyStyles',
    enabled: true,
    phase: 'write',
    fn: applyStyles,
    effect: effect$1,
    requires: ['computeStyles']
  };

  function distanceAndSkiddingToXY(placement, rects, offset) {
    var basePlacement = getBasePlacement(placement);
    var invertDistance = [left, top].indexOf(basePlacement) >= 0 ? -1 : 1;

    var _ref = typeof offset === 'function' ? offset(Object.assign({}, rects, {
      placement: placement
    })) : offset,
        skidding = _ref[0],
        distance = _ref[1];

    skidding = skidding || 0;
    distance = (distance || 0) * invertDistance;
    return [left, right].indexOf(basePlacement) >= 0 ? {
      x: distance,
      y: skidding
    } : {
      x: skidding,
      y: distance
    };
  }

  function offset(_ref2) {
    var state = _ref2.state,
        options = _ref2.options,
        name = _ref2.name;
    var _options$offset = options.offset,
        offset = _options$offset === void 0 ? [0, 0] : _options$offset;
    var data = placements.reduce(function (acc, placement) {
      acc[placement] = distanceAndSkiddingToXY(placement, state.rects, offset);
      return acc;
    }, {});
    var _data$state$placement = data[state.placement],
        x = _data$state$placement.x,
        y = _data$state$placement.y;

    if (state.modifiersData.popperOffsets != null) {
      state.modifiersData.popperOffsets.x += x;
      state.modifiersData.popperOffsets.y += y;
    }

    state.modifiersData[name] = data;
  } // eslint-disable-next-line import/no-unused-modules


  var offset$1 = {
    name: 'offset',
    enabled: true,
    phase: 'main',
    requires: ['popperOffsets'],
    fn: offset
  };

  var hash$1 = {
    left: 'right',
    right: 'left',
    bottom: 'top',
    top: 'bottom'
  };
  function getOppositePlacement(placement) {
    return placement.replace(/left|right|bottom|top/g, function (matched) {
      return hash$1[matched];
    });
  }

  var hash = {
    start: 'end',
    end: 'start'
  };
  function getOppositeVariationPlacement(placement) {
    return placement.replace(/start|end/g, function (matched) {
      return hash[matched];
    });
  }

  function computeAutoPlacement(state, options) {
    if (options === void 0) {
      options = {};
    }

    var _options = options,
        placement = _options.placement,
        boundary = _options.boundary,
        rootBoundary = _options.rootBoundary,
        padding = _options.padding,
        flipVariations = _options.flipVariations,
        _options$allowedAutoP = _options.allowedAutoPlacements,
        allowedAutoPlacements = _options$allowedAutoP === void 0 ? placements : _options$allowedAutoP;
    var variation = getVariation(placement);
    var placements$1 = variation ? flipVariations ? variationPlacements : variationPlacements.filter(function (placement) {
      return getVariation(placement) === variation;
    }) : basePlacements;
    var allowedPlacements = placements$1.filter(function (placement) {
      return allowedAutoPlacements.indexOf(placement) >= 0;
    });

    if (allowedPlacements.length === 0) {
      allowedPlacements = placements$1;

      {
        console.error(['Popper: The `allowedAutoPlacements` option did not allow any', 'placements. Ensure the `placement` option matches the variation', 'of the allowed placements.', 'For example, "auto" cannot be used to allow "bottom-start".', 'Use "auto-start" instead.'].join(' '));
      }
    } // $FlowFixMe[incompatible-type]: Flow seems to have problems with two array unions...


    var overflows = allowedPlacements.reduce(function (acc, placement) {
      acc[placement] = detectOverflow(state, {
        placement: placement,
        boundary: boundary,
        rootBoundary: rootBoundary,
        padding: padding
      })[getBasePlacement(placement)];
      return acc;
    }, {});
    return Object.keys(overflows).sort(function (a, b) {
      return overflows[a] - overflows[b];
    });
  }

  function getExpandedFallbackPlacements(placement) {
    if (getBasePlacement(placement) === auto) {
      return [];
    }

    var oppositePlacement = getOppositePlacement(placement);
    return [getOppositeVariationPlacement(placement), oppositePlacement, getOppositeVariationPlacement(oppositePlacement)];
  }

  function flip(_ref) {
    var state = _ref.state,
        options = _ref.options,
        name = _ref.name;

    if (state.modifiersData[name]._skip) {
      return;
    }

    var _options$mainAxis = options.mainAxis,
        checkMainAxis = _options$mainAxis === void 0 ? true : _options$mainAxis,
        _options$altAxis = options.altAxis,
        checkAltAxis = _options$altAxis === void 0 ? true : _options$altAxis,
        specifiedFallbackPlacements = options.fallbackPlacements,
        padding = options.padding,
        boundary = options.boundary,
        rootBoundary = options.rootBoundary,
        altBoundary = options.altBoundary,
        _options$flipVariatio = options.flipVariations,
        flipVariations = _options$flipVariatio === void 0 ? true : _options$flipVariatio,
        allowedAutoPlacements = options.allowedAutoPlacements;
    var preferredPlacement = state.options.placement;
    var basePlacement = getBasePlacement(preferredPlacement);
    var isBasePlacement = basePlacement === preferredPlacement;
    var fallbackPlacements = specifiedFallbackPlacements || (isBasePlacement || !flipVariations ? [getOppositePlacement(preferredPlacement)] : getExpandedFallbackPlacements(preferredPlacement));
    var placements = [preferredPlacement].concat(fallbackPlacements).reduce(function (acc, placement) {
      return acc.concat(getBasePlacement(placement) === auto ? computeAutoPlacement(state, {
        placement: placement,
        boundary: boundary,
        rootBoundary: rootBoundary,
        padding: padding,
        flipVariations: flipVariations,
        allowedAutoPlacements: allowedAutoPlacements
      }) : placement);
    }, []);
    var referenceRect = state.rects.reference;
    var popperRect = state.rects.popper;
    var checksMap = new Map();
    var makeFallbackChecks = true;
    var firstFittingPlacement = placements[0];

    for (var i = 0; i < placements.length; i++) {
      var placement = placements[i];

      var _basePlacement = getBasePlacement(placement);

      var isStartVariation = getVariation(placement) === start;
      var isVertical = [top, bottom].indexOf(_basePlacement) >= 0;
      var len = isVertical ? 'width' : 'height';
      var overflow = detectOverflow(state, {
        placement: placement,
        boundary: boundary,
        rootBoundary: rootBoundary,
        altBoundary: altBoundary,
        padding: padding
      });
      var mainVariationSide = isVertical ? isStartVariation ? right : left : isStartVariation ? bottom : top;

      if (referenceRect[len] > popperRect[len]) {
        mainVariationSide = getOppositePlacement(mainVariationSide);
      }

      var altVariationSide = getOppositePlacement(mainVariationSide);
      var checks = [];

      if (checkMainAxis) {
        checks.push(overflow[_basePlacement] <= 0);
      }

      if (checkAltAxis) {
        checks.push(overflow[mainVariationSide] <= 0, overflow[altVariationSide] <= 0);
      }

      if (checks.every(function (check) {
        return check;
      })) {
        firstFittingPlacement = placement;
        makeFallbackChecks = false;
        break;
      }

      checksMap.set(placement, checks);
    }

    if (makeFallbackChecks) {
      // `2` may be desired in some cases – research later
      var numberOfChecks = flipVariations ? 3 : 1;

      var _loop = function _loop(_i) {
        var fittingPlacement = placements.find(function (placement) {
          var checks = checksMap.get(placement);

          if (checks) {
            return checks.slice(0, _i).every(function (check) {
              return check;
            });
          }
        });

        if (fittingPlacement) {
          firstFittingPlacement = fittingPlacement;
          return "break";
        }
      };

      for (var _i = numberOfChecks; _i > 0; _i--) {
        var _ret = _loop(_i);

        if (_ret === "break") break;
      }
    }

    if (state.placement !== firstFittingPlacement) {
      state.modifiersData[name]._skip = true;
      state.placement = firstFittingPlacement;
      state.reset = true;
    }
  } // eslint-disable-next-line import/no-unused-modules


  var flip$1 = {
    name: 'flip',
    enabled: true,
    phase: 'main',
    fn: flip,
    requiresIfExists: ['offset'],
    data: {
      _skip: false
    }
  };

  function getAltAxis(axis) {
    return axis === 'x' ? 'y' : 'x';
  }

  function within(min$1, value, max$1) {
    return max(min$1, min(value, max$1));
  }
  function withinMaxClamp(min, value, max) {
    var v = within(min, value, max);
    return v > max ? max : v;
  }

  function preventOverflow(_ref) {
    var state = _ref.state,
        options = _ref.options,
        name = _ref.name;
    var _options$mainAxis = options.mainAxis,
        checkMainAxis = _options$mainAxis === void 0 ? true : _options$mainAxis,
        _options$altAxis = options.altAxis,
        checkAltAxis = _options$altAxis === void 0 ? false : _options$altAxis,
        boundary = options.boundary,
        rootBoundary = options.rootBoundary,
        altBoundary = options.altBoundary,
        padding = options.padding,
        _options$tether = options.tether,
        tether = _options$tether === void 0 ? true : _options$tether,
        _options$tetherOffset = options.tetherOffset,
        tetherOffset = _options$tetherOffset === void 0 ? 0 : _options$tetherOffset;
    var overflow = detectOverflow(state, {
      boundary: boundary,
      rootBoundary: rootBoundary,
      padding: padding,
      altBoundary: altBoundary
    });
    var basePlacement = getBasePlacement(state.placement);
    var variation = getVariation(state.placement);
    var isBasePlacement = !variation;
    var mainAxis = getMainAxisFromPlacement(basePlacement);
    var altAxis = getAltAxis(mainAxis);
    var popperOffsets = state.modifiersData.popperOffsets;
    var referenceRect = state.rects.reference;
    var popperRect = state.rects.popper;
    var tetherOffsetValue = typeof tetherOffset === 'function' ? tetherOffset(Object.assign({}, state.rects, {
      placement: state.placement
    })) : tetherOffset;
    var normalizedTetherOffsetValue = typeof tetherOffsetValue === 'number' ? {
      mainAxis: tetherOffsetValue,
      altAxis: tetherOffsetValue
    } : Object.assign({
      mainAxis: 0,
      altAxis: 0
    }, tetherOffsetValue);
    var offsetModifierState = state.modifiersData.offset ? state.modifiersData.offset[state.placement] : null;
    var data = {
      x: 0,
      y: 0
    };

    if (!popperOffsets) {
      return;
    }

    if (checkMainAxis) {
      var _offsetModifierState$;

      var mainSide = mainAxis === 'y' ? top : left;
      var altSide = mainAxis === 'y' ? bottom : right;
      var len = mainAxis === 'y' ? 'height' : 'width';
      var offset = popperOffsets[mainAxis];
      var min$1 = offset + overflow[mainSide];
      var max$1 = offset - overflow[altSide];
      var additive = tether ? -popperRect[len] / 2 : 0;
      var minLen = variation === start ? referenceRect[len] : popperRect[len];
      var maxLen = variation === start ? -popperRect[len] : -referenceRect[len]; // We need to include the arrow in the calculation so the arrow doesn't go
      // outside the reference bounds

      var arrowElement = state.elements.arrow;
      var arrowRect = tether && arrowElement ? getLayoutRect(arrowElement) : {
        width: 0,
        height: 0
      };
      var arrowPaddingObject = state.modifiersData['arrow#persistent'] ? state.modifiersData['arrow#persistent'].padding : getFreshSideObject();
      var arrowPaddingMin = arrowPaddingObject[mainSide];
      var arrowPaddingMax = arrowPaddingObject[altSide]; // If the reference length is smaller than the arrow length, we don't want
      // to include its full size in the calculation. If the reference is small
      // and near the edge of a boundary, the popper can overflow even if the
      // reference is not overflowing as well (e.g. virtual elements with no
      // width or height)

      var arrowLen = within(0, referenceRect[len], arrowRect[len]);
      var minOffset = isBasePlacement ? referenceRect[len] / 2 - additive - arrowLen - arrowPaddingMin - normalizedTetherOffsetValue.mainAxis : minLen - arrowLen - arrowPaddingMin - normalizedTetherOffsetValue.mainAxis;
      var maxOffset = isBasePlacement ? -referenceRect[len] / 2 + additive + arrowLen + arrowPaddingMax + normalizedTetherOffsetValue.mainAxis : maxLen + arrowLen + arrowPaddingMax + normalizedTetherOffsetValue.mainAxis;
      var arrowOffsetParent = state.elements.arrow && getOffsetParent(state.elements.arrow);
      var clientOffset = arrowOffsetParent ? mainAxis === 'y' ? arrowOffsetParent.clientTop || 0 : arrowOffsetParent.clientLeft || 0 : 0;
      var offsetModifierValue = (_offsetModifierState$ = offsetModifierState == null ? void 0 : offsetModifierState[mainAxis]) != null ? _offsetModifierState$ : 0;
      var tetherMin = offset + minOffset - offsetModifierValue - clientOffset;
      var tetherMax = offset + maxOffset - offsetModifierValue;
      var preventedOffset = within(tether ? min(min$1, tetherMin) : min$1, offset, tether ? max(max$1, tetherMax) : max$1);
      popperOffsets[mainAxis] = preventedOffset;
      data[mainAxis] = preventedOffset - offset;
    }

    if (checkAltAxis) {
      var _offsetModifierState$2;

      var _mainSide = mainAxis === 'x' ? top : left;

      var _altSide = mainAxis === 'x' ? bottom : right;

      var _offset = popperOffsets[altAxis];

      var _len = altAxis === 'y' ? 'height' : 'width';

      var _min = _offset + overflow[_mainSide];

      var _max = _offset - overflow[_altSide];

      var isOriginSide = [top, left].indexOf(basePlacement) !== -1;

      var _offsetModifierValue = (_offsetModifierState$2 = offsetModifierState == null ? void 0 : offsetModifierState[altAxis]) != null ? _offsetModifierState$2 : 0;

      var _tetherMin = isOriginSide ? _min : _offset - referenceRect[_len] - popperRect[_len] - _offsetModifierValue + normalizedTetherOffsetValue.altAxis;

      var _tetherMax = isOriginSide ? _offset + referenceRect[_len] + popperRect[_len] - _offsetModifierValue - normalizedTetherOffsetValue.altAxis : _max;

      var _preventedOffset = tether && isOriginSide ? withinMaxClamp(_tetherMin, _offset, _tetherMax) : within(tether ? _tetherMin : _min, _offset, tether ? _tetherMax : _max);

      popperOffsets[altAxis] = _preventedOffset;
      data[altAxis] = _preventedOffset - _offset;
    }

    state.modifiersData[name] = data;
  } // eslint-disable-next-line import/no-unused-modules


  var preventOverflow$1 = {
    name: 'preventOverflow',
    enabled: true,
    phase: 'main',
    fn: preventOverflow,
    requiresIfExists: ['offset']
  };

  var toPaddingObject = function toPaddingObject(padding, state) {
    padding = typeof padding === 'function' ? padding(Object.assign({}, state.rects, {
      placement: state.placement
    })) : padding;
    return mergePaddingObject(typeof padding !== 'number' ? padding : expandToHashMap(padding, basePlacements));
  };

  function arrow(_ref) {
    var _state$modifiersData$;

    var state = _ref.state,
        name = _ref.name,
        options = _ref.options;
    var arrowElement = state.elements.arrow;
    var popperOffsets = state.modifiersData.popperOffsets;
    var basePlacement = getBasePlacement(state.placement);
    var axis = getMainAxisFromPlacement(basePlacement);
    var isVertical = [left, right].indexOf(basePlacement) >= 0;
    var len = isVertical ? 'height' : 'width';

    if (!arrowElement || !popperOffsets) {
      return;
    }

    var paddingObject = toPaddingObject(options.padding, state);
    var arrowRect = getLayoutRect(arrowElement);
    var minProp = axis === 'y' ? top : left;
    var maxProp = axis === 'y' ? bottom : right;
    var endDiff = state.rects.reference[len] + state.rects.reference[axis] - popperOffsets[axis] - state.rects.popper[len];
    var startDiff = popperOffsets[axis] - state.rects.reference[axis];
    var arrowOffsetParent = getOffsetParent(arrowElement);
    var clientSize = arrowOffsetParent ? axis === 'y' ? arrowOffsetParent.clientHeight || 0 : arrowOffsetParent.clientWidth || 0 : 0;
    var centerToReference = endDiff / 2 - startDiff / 2; // Make sure the arrow doesn't overflow the popper if the center point is
    // outside of the popper bounds

    var min = paddingObject[minProp];
    var max = clientSize - arrowRect[len] - paddingObject[maxProp];
    var center = clientSize / 2 - arrowRect[len] / 2 + centerToReference;
    var offset = within(min, center, max); // Prevents breaking syntax highlighting...

    var axisProp = axis;
    state.modifiersData[name] = (_state$modifiersData$ = {}, _state$modifiersData$[axisProp] = offset, _state$modifiersData$.centerOffset = offset - center, _state$modifiersData$);
  }

  function effect(_ref2) {
    var state = _ref2.state,
        options = _ref2.options;
    var _options$element = options.element,
        arrowElement = _options$element === void 0 ? '[data-popper-arrow]' : _options$element;

    if (arrowElement == null) {
      return;
    } // CSS selector


    if (typeof arrowElement === 'string') {
      arrowElement = state.elements.popper.querySelector(arrowElement);

      if (!arrowElement) {
        return;
      }
    }

    {
      if (!isHTMLElement(arrowElement)) {
        console.error(['Popper: "arrow" element must be an HTMLElement (not an SVGElement).', 'To use an SVG arrow, wrap it in an HTMLElement that will be used as', 'the arrow.'].join(' '));
      }
    }

    if (!contains(state.elements.popper, arrowElement)) {
      {
        console.error(['Popper: "arrow" modifier\'s `element` must be a child of the popper', 'element.'].join(' '));
      }

      return;
    }

    state.elements.arrow = arrowElement;
  } // eslint-disable-next-line import/no-unused-modules


  var arrow$1 = {
    name: 'arrow',
    enabled: true,
    phase: 'main',
    fn: arrow,
    effect: effect,
    requires: ['popperOffsets'],
    requiresIfExists: ['preventOverflow']
  };

  function getSideOffsets(overflow, rect, preventedOffsets) {
    if (preventedOffsets === void 0) {
      preventedOffsets = {
        x: 0,
        y: 0
      };
    }

    return {
      top: overflow.top - rect.height - preventedOffsets.y,
      right: overflow.right - rect.width + preventedOffsets.x,
      bottom: overflow.bottom - rect.height + preventedOffsets.y,
      left: overflow.left - rect.width - preventedOffsets.x
    };
  }

  function isAnySideFullyClipped(overflow) {
    return [top, right, bottom, left].some(function (side) {
      return overflow[side] >= 0;
    });
  }

  function hide(_ref) {
    var state = _ref.state,
        name = _ref.name;
    var referenceRect = state.rects.reference;
    var popperRect = state.rects.popper;
    var preventedOffsets = state.modifiersData.preventOverflow;
    var referenceOverflow = detectOverflow(state, {
      elementContext: 'reference'
    });
    var popperAltOverflow = detectOverflow(state, {
      altBoundary: true
    });
    var referenceClippingOffsets = getSideOffsets(referenceOverflow, referenceRect);
    var popperEscapeOffsets = getSideOffsets(popperAltOverflow, popperRect, preventedOffsets);
    var isReferenceHidden = isAnySideFullyClipped(referenceClippingOffsets);
    var hasPopperEscaped = isAnySideFullyClipped(popperEscapeOffsets);
    state.modifiersData[name] = {
      referenceClippingOffsets: referenceClippingOffsets,
      popperEscapeOffsets: popperEscapeOffsets,
      isReferenceHidden: isReferenceHidden,
      hasPopperEscaped: hasPopperEscaped
    };
    state.attributes.popper = Object.assign({}, state.attributes.popper, {
      'data-popper-reference-hidden': isReferenceHidden,
      'data-popper-escaped': hasPopperEscaped
    });
  } // eslint-disable-next-line import/no-unused-modules


  var hide$1 = {
    name: 'hide',
    enabled: true,
    phase: 'main',
    requiresIfExists: ['preventOverflow'],
    fn: hide
  };

  var defaultModifiers$1 = [eventListeners, popperOffsets$1, computeStyles$1, applyStyles$1];
  var createPopper$1 = /*#__PURE__*/popperGenerator({
    defaultModifiers: defaultModifiers$1
  }); // eslint-disable-next-line import/no-unused-modules

  var defaultModifiers = [eventListeners, popperOffsets$1, computeStyles$1, applyStyles$1, offset$1, flip$1, preventOverflow$1, arrow$1, hide$1];
  var createPopper = /*#__PURE__*/popperGenerator({
    defaultModifiers: defaultModifiers
  }); // eslint-disable-next-line import/no-unused-modules

  exports.applyStyles = applyStyles$1;
  exports.arrow = arrow$1;
  exports.computeStyles = computeStyles$1;
  exports.createPopper = createPopper;
  exports.createPopperLite = createPopper$1;
  exports.defaultModifiers = defaultModifiers;
  exports.detectOverflow = detectOverflow;
  exports.eventListeners = eventListeners;
  exports.flip = flip$1;
  exports.hide = hide$1;
  exports.offset = offset$1;
  exports.popperGenerator = popperGenerator;
  exports.popperOffsets = popperOffsets$1;
  exports.preventOverflow = preventOverflow$1;

  Object.defineProperty(exports, '__esModule', { value: true });

})));
//# sourceMappingURL=popper.js.map

/*!
  * AXAdesignSystemLib  v1.0.0 (https://designsystem.axa.com)
  * Copyright 2021-2023 The AXA Design System Team
  * Licensed under MIT
  * This a fork of Bootstrap : Initial license below
  * Bootstrap v5.1.3 (https://getbootstrap.com/)
  * Copyright 2011-2023 The Bootstrap Authors (https://github.com/twbs/bootstrap/graphs/contributors)
  * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
*/ !function(e,t){"object"==typeof exports&&"undefined"!=typeof module?module.exports=t():"function"==typeof define&&define.amd?define(t):(e="undefined"!=typeof globalThis?globalThis:e||self).bootstrap=t()}(this,function(){"use strict";let e="transitionend",t=e=>null==e?`${e}`:({}).toString.call(e).match(/\s([a-z]+)/i)[1].toLowerCase(),i=e=>{do e+=Math.floor(1e6*Math.random());while(document.getElementById(e));return e},n=e=>{let t=e.getAttribute("data-bs-target");if(!t||"#"===t){let i=e.getAttribute("href");if(!i||!i.includes("#")&&!i.startsWith("."))return null;i.includes("#")&&!i.startsWith("#")&&(i=`#${i.split("#")[1]}`),t=i&&"#"!==i?i.trim():null}return t},s=e=>{let t=n(e);return t&&document.querySelector(t)?t:null},r=e=>{let t=n(e);return t?document.querySelector(t):null},o=e=>{if(!e)return 0;let{transitionDuration:t,transitionDelay:i}=window.getComputedStyle(e),n=Number.parseFloat(t),s=Number.parseFloat(i);return n||s?(t=t.split(",")[0],i=i.split(",")[0],(Number.parseFloat(t)+Number.parseFloat(i))*1e3):0},a=t=>{t.dispatchEvent(new Event(e))},l=e=>!!e&&"object"==typeof e&&(void 0!==e.jquery&&(e=e[0]),void 0!==e.nodeType),c=e=>l(e)?e.jquery?e[0]:e:"string"==typeof e&&e.length>0?document.querySelector(e):null,h=(e,i,n)=>{Object.keys(n).forEach(s=>{let r=n[s],o=i[s],a=o&&l(o)?"element":t(o);if(!RegExp(r).test(a))throw TypeError(`${e.toUpperCase()}: Option "${s}" provided type "${a}" but expected type "${r}".`)})},d=e=>!!l(e)&&0!==e.getClientRects().length&&"visible"===getComputedStyle(e).getPropertyValue("visibility"),u=e=>!!(!e||e.nodeType!==Node.ELEMENT_NODE||e.classList.contains("disabled"))||(void 0!==e.disabled?e.disabled:e.hasAttribute("disabled")&&"false"!==e.getAttribute("disabled")),f=e=>{if(!document.documentElement.attachShadow)return null;if("function"==typeof e.getRootNode){let t=e.getRootNode();return t instanceof ShadowRoot?t:null}return e instanceof ShadowRoot?e:e.parentNode?f(e.parentNode):null},p=()=>{},g=e=>{e.offsetHeight},m=()=>{let{jQuery:e}=window;return e&&!document.body.hasAttribute("data-bs-no-jquery")?e:null},v=[],b=e=>{"loading"===document.readyState?(v.length||document.addEventListener("DOMContentLoaded",()=>{v.forEach(e=>e())}),v.push(e)):e()},y=()=>"rtl"===document.documentElement.dir,E=e=>{b(()=>{let t=m();if(t){let i=e.NAME,n=t.fn[i];t.fn[i]=e.jQueryInterface,t.fn[i].Constructor=e,t.fn[i].noConflict=()=>(t.fn[i]=n,e.jQueryInterface)}})},$=e=>{"function"==typeof e&&e()},w=(t,i,n=!0)=>{if(!n){$(t);return}let s=o(i)+5,r=!1,l=({target:n})=>{n===i&&(r=!0,i.removeEventListener(e,l),$(t))};i.addEventListener(e,l),setTimeout(()=>{r||a(i)},s)},A=(e,t,i,n)=>{let s=e.indexOf(t);if(-1===s)return e[!i&&n?e.length-1:0];let r=e.length;return s+=i?1:-1,n&&(s=(s+r)%r),e[Math.max(0,Math.min(s,r-1))]},C=/[^.]*(?=\..*)\.|.*/,T=/\..*/,L=/::\d+$/,x={},k=1,O={mouseenter:"mouseover",mouseleave:"mouseout",mouseleaveall:"mouseleave"},D=/^(mouseenter|mouseleave)/i,S=new Set(["click","dblclick","mouseup","mousedown","contextmenu","mousewheel","DOMMouseScroll","mouseover","mouseout","mouseleave","mousemove","selectstart","selectend","keydown","keypress","keyup","orientationchange","touchstart","touchmove","touchend","touchcancel","pointerdown","pointermove","pointerup","pointerleave","pointercancel","gesturestart","gesturechange","gestureend","focus","blur","change","reset","select","submit","focusin","focusout","load","unload","beforeunload","resize","move","DOMContentLoaded","readystatechange","error","abort","scroll"]);function N(e,t){return t&&`${t}::${k++}`||e.uidEvent||k++}function I(e){let t=N(e);return e.uidEvent=t,x[t]=x[t]||{},x[t]}function _(e,t,i=null){let n=Object.keys(e);for(let s=0,r=n.length;s<r;s++){let o=e[n[s]];if(o.originalHandler===t&&o.delegationSelector===i)return o}return null}function P(e,t,i){let n="string"==typeof t,s=B(e),r=S.has(s);return r||(s=e),[n,n?i:t,s]}function M(e,t,i,n,s){var r,o,a,l,c;if("string"!=typeof t||!e)return;if(i||(i=n,n=null),D.test(t)){let h=e=>function(t){if(!t.relatedTarget||t.relatedTarget!==t.delegateTarget&&!t.delegateTarget.contains(t.relatedTarget))return e.call(this,t)};n?n=h(n):i=h(i)}let[d,u,f]=P(t,i,n),p=I(e),g=p[f]||(p[f]={}),m=_(g,u,d?i:null);if(m){m.oneOff=m.oneOff&&s;return}let v=N(u,t.replace(C,"")),b=d?(r=e,o=i,a=n,function e(t){let i=r.querySelectorAll(o);for(let{target:n}=t;n&&n!==this;n=n.parentNode)for(let s=i.length;s--;)if(i[s]===n)return t.delegateTarget=n,e.oneOff&&j.off(r,t.type,o,a),a.apply(n,[t]);return null}):(l=e,c=i,function e(t){return t.delegateTarget=l,e.oneOff&&j.off(l,t.type,c),c.apply(l,[t])});b.delegationSelector=d?i:null,b.originalHandler=u,b.oneOff=s,b.uidEvent=v,g[v]=b,e.addEventListener(f,b,d)}function H(e,t,i,n,s){let r=_(t[i],n,s);r&&(e.removeEventListener(i,r,Boolean(s)),delete t[i][r.uidEvent])}function B(e){return O[e=e.replace(T,"")]||e}let j={on(e,t,i,n){M(e,t,i,n,!1)},one(e,t,i,n){M(e,t,i,n,!0)},off(e,t,i,n){if("string"!=typeof t||!e)return;let[s,r,o]=P(t,i,n),a=o!==t,l=I(e),c=t.startsWith(".");if(void 0!==r){if(!l||!l[o])return;H(e,l,o,r,s?i:null);return}c&&Object.keys(l).forEach(i=>{!function e(t,i,n,s){let r=i[n]||{};Object.keys(r).forEach(e=>{if(e.includes(s)){let o=r[e];H(t,i,n,o.originalHandler,o.delegationSelector)}})}(e,l,i,t.slice(1))});let h=l[o]||{};Object.keys(h).forEach(i=>{let n=i.replace(L,"");if(!a||t.includes(n)){let s=h[i];H(e,l,o,s.originalHandler,s.delegationSelector)}})},trigger(e,t,i){if("string"!=typeof t||!e)return null;let n=m(),s=B(t),r=S.has(s),o,a=!0,l=!0,c=!1,h=null;return t!==s&&n&&(o=n.Event(t,i),n(e).trigger(o),a=!o.isPropagationStopped(),l=!o.isImmediatePropagationStopped(),c=o.isDefaultPrevented()),r?(h=document.createEvent("HTMLEvents")).initEvent(s,a,!0):h=new CustomEvent(t,{bubbles:a,cancelable:!0}),void 0!==i&&Object.keys(i).forEach(e=>{Object.defineProperty(h,e,{get:()=>i[e]})}),c&&h.preventDefault(),l&&e.dispatchEvent(h),h.defaultPrevented&&void 0!==o&&o.preventDefault(),h}},W=new Map,R={set(e,t,i){W.has(e)||W.set(e,new Map);let n=W.get(e);if(!n.has(t)&&0!==n.size){console.error(`Bootstrap doesn't allow more than one instance per element. Bound instance: ${Array.from(n.keys())[0]}.`);return}n.set(t,i)},get:(e,t)=>W.has(e)&&W.get(e).get(t)||null,remove(e,t){if(!W.has(e))return;let i=W.get(e);i.delete(t),0===i.size&&W.delete(e)}};class z{constructor(e){if(!(e=c(e)))return;this._element=e,R.set(this._element,this.constructor.DATA_KEY,this)}dispose(){R.remove(this._element,this.constructor.DATA_KEY),j.off(this._element,this.constructor.EVENT_KEY),Object.getOwnPropertyNames(this).forEach(e=>{this[e]=null})}_queueCallback(e,t,i=!0){w(e,t,i)}static getInstance(e){return R.get(c(e),this.DATA_KEY)}static getOrCreateInstance(e,t={}){return this.getInstance(e)||new this(e,"object"==typeof t?t:null)}static get VERSION(){return"5.1.3"}static get NAME(){throw Error('You have to implement the static method "NAME", for each component!')}static get DATA_KEY(){return`bs.${this.NAME}`}static get EVENT_KEY(){return`.${this.DATA_KEY}`}}let q=(e,t="hide")=>{let i=`click.dismiss${e.EVENT_KEY}`,n=e.NAME;j.on(document,i,`[data-bs-dismiss="${n}"]`,function(i){if(["A","AREA"].includes(this.tagName)&&i.preventDefault(),u(this))return;let s=r(this)||this.closest(`.${n}`),o=e.getOrCreateInstance(s);o[t]()})},F=".bs.alert",U=`close${F}`,V=`closed${F}`;class K extends z{static get NAME(){return"alert"}close(){let e=j.trigger(this._element,U);if(e.defaultPrevented)return;this._element.classList.remove("show");let t=this._element.classList.contains("fade");this._queueCallback(()=>this._destroyElement(),this._element,t)}_destroyElement(){this._element.remove(),j.trigger(this._element,V),this.dispose()}static jQueryInterface(e){return this.each(function(){let t=K.getOrCreateInstance(this);if("string"==typeof e){if(void 0===t[e]||e.startsWith("_")||"constructor"===e)throw TypeError(`No method named "${e}"`);t[e](this)}})}}q(K,"close"),E(K);let X='[data-bs-toggle="button"]',Y="click.bs.button.data-api";class Q extends z{static get NAME(){return"button"}toggle(){this._element.setAttribute("aria-pressed",this._element.classList.toggle("active"))}static jQueryInterface(e){return this.each(function(){let t=Q.getOrCreateInstance(this);"toggle"===e&&t[e]()})}}function G(e){return"true"===e||"false"!==e&&(e===Number(e).toString()?Number(e):""===e||"null"===e?null:e)}function Z(e){return e.replace(/[A-Z]/g,e=>`-${e.toLowerCase()}`)}j.on(document,Y,X,e=>{e.preventDefault();let t=e.target.closest(X),i=Q.getOrCreateInstance(t);i.toggle()}),E(Q);let J={setDataAttribute(e,t,i){e.setAttribute(`data-bs-${Z(t)}`,i)},removeDataAttribute(e,t){e.removeAttribute(`data-bs-${Z(t)}`)},getDataAttributes(e){if(!e)return{};let t={};return Object.keys(e.dataset).filter(e=>e.startsWith("bs")).forEach(i=>{let n=i.replace(/^bs/,"");t[n=n.charAt(0).toLowerCase()+n.slice(1,n.length)]=G(e.dataset[i])}),t},getDataAttribute:(e,t)=>G(e.getAttribute(`data-bs-${Z(t)}`)),offset(e){let t=e.getBoundingClientRect();return{top:t.top+window.pageYOffset,left:t.left+window.pageXOffset}},position:e=>({top:e.offsetTop,left:e.offsetLeft})},ee={find:(e,t=document.documentElement)=>[].concat(...Element.prototype.querySelectorAll.call(t,e)),findOne:(e,t=document.documentElement)=>Element.prototype.querySelector.call(t,e),children:(e,t)=>[].concat(...e.children).filter(e=>e.matches(t)),parents(e,t){let i=[],n=e.parentNode;for(;n&&n.nodeType===Node.ELEMENT_NODE&&3!==n.nodeType;)n.matches(t)&&i.push(n),n=n.parentNode;return i},prev(e,t){let i=e.previousElementSibling;for(;i;){if(i.matches(t))return[i];i=i.previousElementSibling}return[]},next(e,t){let i=e.nextElementSibling;for(;i;){if(i.matches(t))return[i];i=i.nextElementSibling}return[]},focusableChildren(e){let t=["a","button","input","textarea","select","details","[tabindex]",'[contenteditable="true"]'].map(e=>`${e}:not([tabindex^="-"])`).join(", ");return this.find(t,e).filter(e=>!u(e)&&d(e))}},et="carousel",ei=".bs.carousel",en=".data-api",es={interval:5e3,keyboard:!0,slide:!1,pause:"hover",wrap:!0,touch:!0},er={interval:"(number|boolean)",keyboard:"boolean",slide:"(boolean|string)",pause:"(string|boolean)",wrap:"boolean",touch:"boolean"},eo="next",ea="prev",el="left",ec="right",eh={ArrowLeft:ec,ArrowRight:el},ed=`slide${ei}`,eu=`slid${ei}`,ef=`keydown${ei}`,ep=`mouseenter${ei}`,eg=`mouseleave${ei}`,em=`touchstart${ei}`,e8=`touchmove${ei}`,ev=`touchend${ei}`,eb=`pointerdown${ei}`,ey=`pointerup${ei}`,eE=`dragstart${ei}`,e$=`load${ei}${en}`,ew=`click${ei}${en}`,eA="active",eC=".active.carousel-item";class eT extends z{constructor(e,t){super(e),this._items=null,this._interval=null,this._activeElement=null,this._isPaused=!1,this._isSliding=!1,this.touchTimeout=null,this.touchStartX=0,this.touchDeltaX=0,this._config=this._getConfig(t),this._indicatorsElement=ee.findOne(".carousel-indicators",this._element),this._touchSupported="ontouchstart"in document.documentElement||navigator.maxTouchPoints>0,this._pointerEvent=Boolean(window.PointerEvent),this._addEventListeners()}static get Default(){return es}static get NAME(){return et}next(){this._slide(eo)}nextWhenVisible(){!document.hidden&&d(this._element)&&this.next()}prev(){this._slide(ea)}pause(e){e||(this._isPaused=!0),ee.findOne(".carousel-item-next, .carousel-item-prev",this._element)&&(a(this._element),this.cycle(!0)),clearInterval(this._interval),this._interval=null}cycle(e){e||(this._isPaused=!1),this._interval&&(clearInterval(this._interval),this._interval=null),this._config&&this._config.interval&&!this._isPaused&&(this._updateInterval(),this._interval=setInterval((document.visibilityState?this.nextWhenVisible:this.next).bind(this),this._config.interval))}to(e){this._activeElement=ee.findOne(eC,this._element);let t=this._getItemIndex(this._activeElement);if(!(e>this._items.length-1)&&!(e<0)){if(this._isSliding){j.one(this._element,eu,()=>this.to(e));return}if(t===e){this.pause(),this.cycle();return}this._slide(e>t?eo:ea,this._items[e])}}_getConfig(e){return h(et,e={...es,...J.getDataAttributes(this._element),..."object"==typeof e?e:{}},er),e}_handleSwipe(){let e=Math.abs(this.touchDeltaX);if(e<=40)return;let t=e/this.touchDeltaX;this.touchDeltaX=0,t&&this._slide(t>0?ec:el)}_addEventListeners(){this._config.keyboard&&j.on(this._element,ef,e=>this._keydown(e)),"hover"===this._config.pause&&(j.on(this._element,ep,e=>this.pause(e)),j.on(this._element,eg,e=>this.cycle(e))),this._config.touch&&this._touchSupported&&this._addTouchEventListeners()}_addTouchEventListeners(){let e=e=>this._pointerEvent&&("pen"===e.pointerType||"touch"===e.pointerType),t=t=>{e(t)?this.touchStartX=t.clientX:this._pointerEvent||(this.touchStartX=t.touches[0].clientX)},i=e=>{this.touchDeltaX=e.touches&&e.touches.length>1?0:e.touches[0].clientX-this.touchStartX},n=t=>{e(t)&&(this.touchDeltaX=t.clientX-this.touchStartX),this._handleSwipe(),"hover"===this._config.pause&&(this.pause(),this.touchTimeout&&clearTimeout(this.touchTimeout),this.touchTimeout=setTimeout(e=>this.cycle(e),500+this._config.interval))};ee.find(".carousel-item img",this._element).forEach(e=>{j.on(e,eE,e=>e.preventDefault())}),this._pointerEvent?(j.on(this._element,eb,e=>t(e)),j.on(this._element,ey,e=>n(e)),this._element.classList.add("pointer-event")):(j.on(this._element,em,e=>t(e)),j.on(this._element,e8,e=>i(e)),j.on(this._element,ev,e=>n(e)))}_keydown(e){if(/input|textarea/i.test(e.target.tagName))return;let t=eh[e.key];t&&(e.preventDefault(),this._slide(t))}_getItemIndex(e){return this._items=e&&e.parentNode?ee.find(".carousel-item",e.parentNode):[],this._items.indexOf(e)}_getItemByOrder(e,t){return A(this._items,t,e===eo,this._config.wrap)}_triggerSlideEvent(e,t){let i=this._getItemIndex(e),n=this._getItemIndex(ee.findOne(eC,this._element));return j.trigger(this._element,ed,{relatedTarget:e,direction:t,from:n,to:i})}_setActiveIndicatorElement(e){if(this._indicatorsElement){let t=ee.findOne(".active",this._indicatorsElement);t.classList.remove(eA),t.removeAttribute("aria-current");let i=ee.find("[data-bs-target]",this._indicatorsElement);for(let n=0;n<i.length;n++)if(Number.parseInt(i[n].getAttribute("data-bs-slide-to"),10)===this._getItemIndex(e)){i[n].classList.add(eA),i[n].setAttribute("aria-current","true");break}}}_updateInterval(){let e=this._activeElement||ee.findOne(eC,this._element);if(!e)return;let t=Number.parseInt(e.getAttribute("data-bs-interval"),10);t?(this._config.defaultInterval=this._config.defaultInterval||this._config.interval,this._config.interval=t):this._config.interval=this._config.defaultInterval||this._config.interval}_slide(e,t){let i=this._directionToOrder(e),n=ee.findOne(eC,this._element),s=this._getItemIndex(n),r=t||this._getItemByOrder(i,n),o=this._getItemIndex(r),a=Boolean(this._interval),l=i===eo,c=l?"carousel-item-start":"carousel-item-end",h=l?"carousel-item-next":"carousel-item-prev",d=this._orderToDirection(i);if(r&&r.classList.contains(eA)){this._isSliding=!1;return}if(this._isSliding)return;let u=this._triggerSlideEvent(r,d);if(u.defaultPrevented||!n||!r)return;this._isSliding=!0,a&&this.pause(),this._setActiveIndicatorElement(r),this._activeElement=r;let f=()=>{j.trigger(this._element,eu,{relatedTarget:r,direction:d,from:s,to:o})};if(this._element.classList.contains("slide")){r.classList.add(h),g(r),n.classList.add(c),r.classList.add(c);let p=()=>{r.classList.remove(c,h),r.classList.add(eA),n.classList.remove(eA,h,c),this._isSliding=!1,setTimeout(f,0)};this._queueCallback(p,n,!0)}else n.classList.remove(eA),r.classList.add(eA),this._isSliding=!1,f();a&&this.cycle()}_directionToOrder(e){return[ec,el].includes(e)?y()?e===el?ea:eo:e===el?eo:ea:e}_orderToDirection(e){return[eo,ea].includes(e)?y()?e===ea?el:ec:e===ea?ec:el:e}static carouselInterface(e,t){let i=eT.getOrCreateInstance(e,t),{_config:n}=i;"object"==typeof t&&(n={...n,...t});let s="string"==typeof t?t:n.slide;if("number"==typeof t)i.to(t);else if("string"==typeof s){if(void 0===i[s])throw TypeError(`No method named "${s}"`);i[s]()}else n.interval&&n.ride&&(i.pause(),i.cycle())}static jQueryInterface(e){return this.each(function(){eT.carouselInterface(this,e)})}static dataApiClickHandler(e){let t=r(this);if(!t||!t.classList.contains("carousel"))return;let i={...J.getDataAttributes(t),...J.getDataAttributes(this)},n=this.getAttribute("data-bs-slide-to");n&&(i.interval=!1),eT.carouselInterface(t,i),n&&eT.getInstance(t).to(n),e.preventDefault()}}j.on(document,ew,"[data-bs-slide], [data-bs-slide-to]",eT.dataApiClickHandler),j.on(window,e$,()=>{let e=ee.find('[data-bs-ride="carousel"]');for(let t=0,i=e.length;t<i;t++)eT.carouselInterface(e[t],eT.getInstance(e[t]))}),E(eT);let eL="collapse",ex="bs.collapse",ek=`.${ex}`,eO={toggle:!0,parent:null},e9={toggle:"boolean",parent:"(null|element)"},eD=`show${ek}`,eS=`shown${ek}`,eN=`hide${ek}`,eI=`hidden${ek}`,e_=`click${ek}.data-api`,eP="show",eM="collapse",eH="collapsing",eB="collapsed",ej=`:scope .${eM} .${eM}`,eW="toggle-close",eR='[data-bs-toggle="collapse"]';class ez extends z{constructor(e,t){super(e),this._isTransitioning=!1,this._config=this._getConfig(t),this._triggerArray=[];let i=ee.find(eR);for(let n=0,r=i.length;n<r;n++){let o=i[n],a=s(o),l=ee.find(a).filter(e=>e===this._element);null!==a&&l.length&&(this._selector=a,this._triggerArray.push(o))}this._initializeChildren(),this._config.parent||this._addAriaAndCollapsedClass(this._triggerArray,this._isShown()),this._config.toggle&&this.toggle()}static get Default(){return eO}static get NAME(){return eL}toggle(){this._isShown()?(this.hide(),this._element.classList.contains("header-fixed")&&document.getElementsByTagName("body")[0].classList.remove("header-opened")):(this.show(),this._element.classList.contains("header-fixed")&&document.getElementsByTagName("body")[0].classList.add("header-opened"))}show(){if(this._isTransitioning||this._isShown())return;let e=[],t;if(this._config.parent){let i=ee.find(ej,this._config.parent);e=ee.find(".collapse.show, .collapse.collapsing",this._config.parent).filter(e=>!i.includes(e))}let n=ee.findOne(this._selector);if(e.length){let s=e.find(e=>n!==e);if((t=s?ez.getInstance(s):null)&&t._isTransitioning)return}let r=j.trigger(this._element,eD);if(r.defaultPrevented)return;e.forEach(e=>{n!==e&&ez.getOrCreateInstance(e,{toggle:!1}).hide(),t||R.set(e,ex,null)});let o=this._getDimension();this._element.classList.remove(eM),this._element.classList.add(eH),this._element.style[o]=0,this._addAriaAndCollapsedClass(this._triggerArray,!0),this._isTransitioning=!0;let a=()=>{this._isTransitioning=!1,this._element.classList.remove(eH),this._element.classList.add(eM,eP),this._element.style[o]="",j.trigger(this._element,eS)},l=o[0].toUpperCase()+o.slice(1),c=`scroll${l}`;this._queueCallback(a,this._element,!0),this._element.style[o]=`${this._element[c]}px`}hide(){if(this._isTransitioning||!this._isShown())return;let e=j.trigger(this._element,eN);if(e.defaultPrevented)return;let t=this._getDimension();this._element.style[t]=`${this._element.getBoundingClientRect()[t]}px`,g(this._element),this._element.classList.add(eH),this._element.classList.remove(eM,eP);let i=this._triggerArray.length;for(let n=0;n<i;n++){let s=this._triggerArray[n],o=r(s);o&&!this._isShown(o)&&this._addAriaAndCollapsedClass([s],!1)}this._isTransitioning=!0;let a=()=>{this._isTransitioning=!1,this._element.classList.remove(eH),this._element.classList.add(eM),j.trigger(this._element,eI)};this._element.style[t]="",this._queueCallback(a,this._element,!0)}_isShown(e=this._element){return e.classList.contains(eP)}_getConfig(e){return(e={...eO,...J.getDataAttributes(this._element),...e}).toggle=Boolean(e.toggle),e.parent=c(e.parent),h(eL,e,e9),e}_getDimension(){return this._element.classList.contains("collapse-horizontal")?"width":"height"}_initializeChildren(){if(!this._config.parent)return;let e=ee.find(ej,this._config.parent);ee.find(eR,this._config.parent).filter(t=>!e.includes(t)).forEach(e=>{let t=r(e);t&&this._addAriaAndCollapsedClass([e],this._isShown(t))})}_addAriaAndCollapsedClass(e,t){e.length&&e.forEach(e=>{t?e.classList.remove(eB):e.classList.add(eB),e.setAttribute("aria-expanded",t)})}static jQueryInterface(e){return this.each(function(){let t={};"string"==typeof e&&/show|hide/.test(e)&&(t.toggle=!1);let i=ez.getOrCreateInstance(this,t);if("string"==typeof e){if(void 0===i[e])throw TypeError(`No method named "${e}"`);i[e]()}})}}j.on(document,e_,eR,function(e){("A"===e.target.tagName||e.delegateTarget&&"A"===e.delegateTarget.tagName)&&e.preventDefault();let t=s(this),i=ee.find(t);i.forEach(e=>{ez.getOrCreateInstance(e,{toggle:!1}).toggle()})}),j.on(document,e_,'[data-bs-toggle="collapse-all"]',function(e){("A"===e.target.tagName||e.delegateTarget&&"A"===e.delegateTarget.tagName)&&e.preventDefault();let t=s(this),i=ee.find(t);e.srcElement.classList.contains("icon-search")?i.forEach(e=>{e.classList.contains(eP)&&"nav-burger"===e.getAttribute("id")||!e.classList.contains(eP)&&"nav-close"===e.getAttribute("id")?ez.getOrCreateInstance(e,{toggle:!1}).toggle():e.classList.contains(eP)||"inputSearch"!==e.getAttribute("id")||ez.getOrCreateInstance(e,{toggle:!1}).toggle()}):i.forEach(e=>{(e.classList.contains(eP)&&e.classList.contains(eW)||!e.classList.contains(eW))&&ez.getOrCreateInstance(e,{toggle:!1}).toggle()})}),E(ez);var eq="bottom",eF="right",eU="left",eV="auto",eK=["top",eq,eF,eU],eX="start",eY="clippingParents",e0="viewport",eQ="popper",e1="reference",e3=eK.reduce(function(e,t){return e.concat([t+"-"+eX,t+"-end"])},[]),e2=[].concat(eK,[eV]).reduce(function(e,t){return e.concat([t,t+"-"+eX,t+"-end"])},[]),e4="beforeRead",eG="read",eZ="afterRead",e6="beforeMain",eJ="main",e5="afterMain",e7="beforeWrite",te="write",tt="afterWrite",ti=[e4,eG,eZ,e6,eJ,e5,e7,te,tt];function tn(e){return e?(e.nodeName||"").toLowerCase():null}function ts(e){if(null==e)return window;if("[object Window]"!==e.toString()){var t=e.ownerDocument;return t&&t.defaultView||window}return e}function tr(e){var t=ts(e).Element;return e instanceof t||e instanceof Element}function to(e){var t=ts(e).HTMLElement;return e instanceof t||e instanceof HTMLElement}function ta(e){if("undefined"==typeof ShadowRoot)return!1;var t=ts(e).ShadowRoot;return e instanceof t||e instanceof ShadowRoot}let tl={name:"applyStyles",enabled:!0,phase:"write",fn:function e(t){var i=t.state;Object.keys(i.elements).forEach(function(e){var t=i.styles[e]||{},n=i.attributes[e]||{},s=i.elements[e];to(s)&&tn(s)&&(Object.assign(s.style,t),Object.keys(n).forEach(function(e){var t=n[e];!1===t?s.removeAttribute(e):s.setAttribute(e,!0===t?"":t)}))})},effect:function e(t){var i=t.state,n={popper:{position:i.options.strategy,left:"0",top:"0",margin:"0"},arrow:{position:"absolute"},reference:{}};return Object.assign(i.elements.popper.style,n.popper),i.styles=n,i.elements.arrow&&Object.assign(i.elements.arrow.style,n.arrow),function(){Object.keys(i.elements).forEach(function(e){var t=i.elements[e],s=i.attributes[e]||{},r=Object.keys(i.styles.hasOwnProperty(e)?i.styles[e]:n[e]).reduce(function(e,t){return e[t]="",e},{});to(t)&&tn(t)&&(Object.assign(t.style,r),Object.keys(s).forEach(function(e){t.removeAttribute(e)}))})}},requires:["computeStyles"]};function tc(e){return e.split("-")[0]}var th=Math.max,td=Math.min,tu=Math.round;function tf(e,t){void 0===t&&(t=!1);var i=e.getBoundingClientRect(),n=1,s=1;if(to(e)&&t){var r=e.offsetHeight,o=e.offsetWidth;o>0&&(n=tu(i.width)/o||1),r>0&&(s=tu(i.height)/r||1)}return{width:i.width/n,height:i.height/s,top:i.top/s,right:i.right/n,bottom:i.bottom/s,left:i.left/n,x:i.left/n,y:i.top/s}}function tp(e){var t=tf(e),i=e.offsetWidth,n=e.offsetHeight;return 1>=Math.abs(t.width-i)&&(i=t.width),1>=Math.abs(t.height-n)&&(n=t.height),{x:e.offsetLeft,y:e.offsetTop,width:i,height:n}}function tg(e,t){var i=t.getRootNode&&t.getRootNode();if(e.contains(t))return!0;if(i&&ta(i)){var n=t;do{if(n&&e.isSameNode(n))return!0;n=n.parentNode||n.host}while(n)}return!1}function tm(e){return ts(e).getComputedStyle(e)}function t8(e){return["table","td","th"].indexOf(tn(e))>=0}function tv(e){return((tr(e)?e.ownerDocument:e.document)||window.document).documentElement}function tb(e){return"html"===tn(e)?e:e.assignedSlot||e.parentNode||(ta(e)?e.host:null)||tv(e)}function ty(e){return to(e)&&"fixed"!==tm(e).position?e.offsetParent:null}function tE(e){for(var t=ts(e),i=ty(e);i&&t8(i)&&"static"===tm(i).position;)i=ty(i);return i&&("html"===tn(i)||"body"===tn(i)&&"static"===tm(i).position)?t:i||function e(t){var i=-1!==navigator.userAgent.toLowerCase().indexOf("firefox");if(-1!==navigator.userAgent.indexOf("Trident")&&to(t)&&"fixed"===tm(t).position)return null;for(var n=tb(t);to(n)&&0>["html","body"].indexOf(tn(n));){var s=tm(n);if("none"!==s.transform||"none"!==s.perspective||"paint"===s.contain||-1!==["transform","perspective"].indexOf(s.willChange)||i&&"filter"===s.willChange||i&&s.filter&&"none"!==s.filter)return n;n=n.parentNode}return null}(e)||t}function t$(e){return["top","bottom"].indexOf(e)>=0?"x":"y"}function tw(e,t,i){return th(e,td(t,i))}function tA(){return{top:0,right:0,bottom:0,left:0}}function tC(e){return Object.assign({},tA(),e)}function tT(e,t){return t.reduce(function(t,i){return t[i]=e,t},{})}let tL={name:"arrow",enabled:!0,phase:"main",fn:function e(t){var i,n=t.state,s=t.name,r=t.options,o=n.elements.arrow,a=n.modifiersData.popperOffsets,l=tc(n.placement),c=t$(l),h=[eU,eF].indexOf(l)>=0?"height":"width";if(o&&a){var d,u,f=(d=r.padding,u=n,tC("number"!=typeof(d="function"==typeof d?d(Object.assign({},u.rects,{placement:u.placement})):d)?d:tT(d,eK))),p=tp(o),g=n.rects.reference[h]+n.rects.reference[c]-a[c]-n.rects.popper[h],m=a[c]-n.rects.reference[c],v=tE(o),b=v?"y"===c?v.clientHeight||0:v.clientWidth||0:0,y=f["y"===c?"top":eU],E=b-p[h]-f["y"===c?eq:eF],$=b/2-p[h]/2+(g/2-m/2),w=tw(y,$,E),A=c;n.modifiersData[s]=((i={})[A]=w,i.centerOffset=w-$,i)}},effect:function e(t){var i=t.state,n=t.options.element,s=void 0===n?"[data-popper-arrow]":n;null!=s&&("string"!=typeof s||(s=i.elements.popper.querySelector(s)))&&tg(i.elements.popper,s)&&(i.elements.arrow=s)},requires:["popperOffsets"],requiresIfExists:["preventOverflow"]};function tx(e){return e.split("-")[1]}var tk={top:"auto",right:"auto",bottom:"auto",left:"auto"};function tO(e){var t,i,n=e.popper,s=e.popperRect,r=e.placement,o=e.variation,a=e.offsets,l=e.position,c=e.gpuAcceleration,h=e.adaptive,d=e.roundOffsets,u=e.isFixed,f=a.x,p=void 0===f?0:f,g=a.y,m=void 0===g?0:g,v="function"==typeof d?d({x:p,y:m}):{x:p,y:m};p=v.x,m=v.y;var b=a.hasOwnProperty("x"),y=a.hasOwnProperty("y"),E=eU,$="top",w=window;if(h){var A=tE(n),C="clientHeight",T="clientWidth";A===ts(n)&&(A=tv(n),"static"!==tm(A).position&&"absolute"===l&&(C="scrollHeight",T="scrollWidth")),("top"===r||(r===eU||r===eF)&&"end"===o)&&($=eq,m-=(u&&w.visualViewport?w.visualViewport.height:A[C])-s.height,m*=c?1:-1),(r===eU||("top"===r||r===eq)&&"end"===o)&&(E=eF,p-=(u&&w.visualViewport?w.visualViewport.width:A[T])-s.width,p*=c?1:-1)}var L,x,k,O,D=Object.assign({position:l},h&&tk),S=!0===d?(x=(L={x:p,y:m}).x,k=L.y,{x:tu(x*(O=window.devicePixelRatio||1))/O||0,y:tu(k*O)/O||0}):{x:p,y:m};return(p=S.x,m=S.y,c)?Object.assign({},D,((i={})[$]=y?"0":"",i[E]=b?"0":"",i.transform=1>=(w.devicePixelRatio||1)?"translate("+p+"px, "+m+"px)":"translate3d("+p+"px, "+m+"px, 0)",i)):Object.assign({},D,((t={})[$]=y?m+"px":"",t[E]=b?p+"px":"",t.transform="",t))}let t9={name:"computeStyles",enabled:!0,phase:"beforeWrite",fn:function e(t){var i=t.state,n=t.options,s=n.gpuAcceleration,r=n.adaptive,o=n.roundOffsets,a=void 0===o||o,l={placement:tc(i.placement),variation:tx(i.placement),popper:i.elements.popper,popperRect:i.rects.popper,gpuAcceleration:void 0===s||s,isFixed:"fixed"===i.options.strategy};null!=i.modifiersData.popperOffsets&&(i.styles.popper=Object.assign({},i.styles.popper,tO(Object.assign({},l,{offsets:i.modifiersData.popperOffsets,position:i.options.strategy,adaptive:void 0===r||r,roundOffsets:a})))),null!=i.modifiersData.arrow&&(i.styles.arrow=Object.assign({},i.styles.arrow,tO(Object.assign({},l,{offsets:i.modifiersData.arrow,position:"absolute",adaptive:!1,roundOffsets:a})))),i.attributes.popper=Object.assign({},i.attributes.popper,{"data-popper-placement":i.placement})},data:{}};var tD={passive:!0};let tS={name:"eventListeners",enabled:!0,phase:"write",fn:function e(){},effect:function e(t){var i=t.state,n=t.instance,s=t.options,r=s.scroll,o=void 0===r||r,a=s.resize,l=void 0===a||a,c=ts(i.elements.popper),h=[].concat(i.scrollParents.reference,i.scrollParents.popper);return o&&h.forEach(function(e){e.addEventListener("scroll",n.update,tD)}),l&&c.addEventListener("resize",n.update,tD),function(){o&&h.forEach(function(e){e.removeEventListener("scroll",n.update,tD)}),l&&c.removeEventListener("resize",n.update,tD)}},data:{}};var tN={left:"right",right:"left",bottom:"top",top:"bottom"};function tI(e){return e.replace(/left|right|bottom|top/g,function(e){return tN[e]})}var t_={start:"end",end:"start"};function tP(e){return e.replace(/start|end/g,function(e){return t_[e]})}function tM(e){var t,i=ts(e);return{scrollLeft:i.pageXOffset,scrollTop:i.pageYOffset}}function tH(e){return tf(tv(e)).left+tM(e).scrollLeft}function tB(e){var t=tm(e),i=t.overflow,n=t.overflowX,s=t.overflowY;return/auto|scroll|overlay|hidden/.test(i+s+n)}function tj(e,t){void 0===t&&(t=[]);var i,n=function e(t){return["html","body","#document"].indexOf(tn(t))>=0?t.ownerDocument.body:to(t)&&tB(t)?t:e(tb(t))}(e),s=n===(null==(i=e.ownerDocument)?void 0:i.body),r=ts(n),o=s?[r].concat(r.visualViewport||[],tB(n)?n:[]):n,a=t.concat(o);return s?a:a.concat(tj(tb(o)))}function tW(e){return Object.assign({},e,{left:e.x,top:e.y,right:e.x+e.width,bottom:e.y+e.height})}function tR(e,t){var i,n,s,r,o,a,l,c,h,d,u,f,p,g,m,v,b,y,E;return t===e0?tW((n=ts(i=e),s=tv(i),r=n.visualViewport,o=s.clientWidth,a=s.clientHeight,l=0,c=0,r&&(o=r.width,a=r.height,/^((?!chrome|android).)*safari/i.test(navigator.userAgent)||(l=r.offsetLeft,c=r.offsetTop)),{width:o,height:a,x:l+tH(i),y:c})):tr(t)?((d=tf(h=t)).top=d.top+h.clientTop,d.left=d.left+h.clientLeft,d.bottom=d.top+h.clientHeight,d.right=d.left+h.clientWidth,d.width=h.clientWidth,d.height=h.clientHeight,d.x=d.left,d.y=d.top,d):tW((u=tv(e),p=tv(u),g=tM(u),m=null==(f=u.ownerDocument)?void 0:f.body,v=th(p.scrollWidth,p.clientWidth,m?m.scrollWidth:0,m?m.clientWidth:0),b=th(p.scrollHeight,p.clientHeight,m?m.scrollHeight:0,m?m.clientHeight:0),y=-g.scrollLeft+tH(u),E=-g.scrollTop,"rtl"===tm(m||p).direction&&(y+=th(p.clientWidth,m?m.clientWidth:0)-v),{width:v,height:b,x:y,y:E}))}function tz(e){var t,i=e.reference,n=e.element,s=e.placement,r=s?tc(s):null,o=s?tx(s):null,a=i.x+i.width/2-n.width/2,l=i.y+i.height/2-n.height/2;switch(r){case"top":t={x:a,y:i.y-n.height};break;case eq:t={x:a,y:i.y+i.height};break;case eF:t={x:i.x+i.width,y:l};break;case eU:t={x:i.x-n.width,y:l};break;default:t={x:i.x,y:i.y}}var c=r?t$(r):null;if(null!=c){var h="y"===c?"height":"width";switch(o){case eX:t[c]=t[c]-(i[h]/2-n[h]/2);break;case"end":t[c]=t[c]+(i[h]/2-n[h]/2)}}return t}function tq(e,t){void 0===t&&(t={});var i,n,s,r,o,a,l,c,h,d=t,u=d.placement,f=void 0===u?e.placement:u,p=d.boundary,g=d.rootBoundary,m=d.elementContext,v=void 0===m?eQ:m,b=d.altBoundary,y=d.padding,E=void 0===y?0:y,$=tC("number"!=typeof E?E:tT(E,eK)),w=e.rects.popper,A=e.elements[void 0!==b&&b?v===eQ?e1:eQ:v],C=(i=tr(A)?A:A.contextElement||tv(e.elements.popper),n=void 0===p?eY:p,s=void 0===g?e0:g,c=(l=[].concat("clippingParents"===n?(r=i,o=tj(tb(r)),a=["absolute","fixed"].indexOf(tm(r).position)>=0&&to(r)?tE(r):r,tr(a)?o.filter(function(e){return tr(e)&&tg(e,a)&&"body"!==tn(e)}):[]):[].concat(n),[s]))[0],(h=l.reduce(function(e,t){var n=tR(i,t);return e.top=th(n.top,e.top),e.right=td(n.right,e.right),e.bottom=td(n.bottom,e.bottom),e.left=th(n.left,e.left),e},tR(i,c))).width=h.right-h.left,h.height=h.bottom-h.top,h.x=h.left,h.y=h.top,h),T=tf(e.elements.reference),L=tz({reference:T,element:w,strategy:"absolute",placement:f}),x=tW(Object.assign({},w,L)),k=v===eQ?x:T,O={top:C.top-k.top+$.top,bottom:k.bottom-C.bottom+$.bottom,left:C.left-k.left+$.left,right:k.right-C.right+$.right},D=e.modifiersData.offset;if(v===eQ&&D){var S=D[f];Object.keys(O).forEach(function(e){var t=[eF,eq].indexOf(e)>=0?1:-1,i=["top",eq].indexOf(e)>=0?"y":"x";O[e]+=S[i]*t})}return O}let tF={name:"flip",enabled:!0,phase:"main",fn:function e(t){var i=t.state,n=t.options,s=t.name;if(!i.modifiersData[s]._skip){for(var r=n.mainAxis,o=void 0===r||r,a=n.altAxis,l=void 0===a||a,c=n.fallbackPlacements,h=n.padding,d=n.boundary,u=n.rootBoundary,f=n.altBoundary,p=n.flipVariations,g=void 0===p||p,m=n.allowedAutoPlacements,v=i.options.placement,b=tc(v),y=[v].concat(c||(b!==v&&g?function e(t){if(tc(t)===eV)return[];var i=tI(t);return[tP(t),i,tP(i)]}(v):[tI(v)])).reduce(function(e,t){var n,s,r,o,a,l,c,f,p,v,b,y,E,$;return e.concat(tc(t)===eV?(n=i,s={placement:t,boundary:d,rootBoundary:u,padding:h,flipVariations:g,allowedAutoPlacements:m},o=(r=s).placement,a=r.boundary,l=r.rootBoundary,c=r.padding,f=r.flipVariations,v=void 0===(p=r.allowedAutoPlacements)?e2:p,0===(E=(y=(b=tx(o))?f?e3:e3.filter(function(e){return tx(e)===b}):eK).filter(function(e){return v.indexOf(e)>=0})).length&&(E=y),Object.keys($=E.reduce(function(e,t){return e[t]=tq(n,{placement:t,boundary:a,rootBoundary:l,padding:c})[tc(t)],e},{})).sort(function(e,t){return $[e]-$[t]})):t)},[]),E=i.rects.reference,$=i.rects.popper,w=new Map,A=!0,C=y[0],T=0;T<y.length;T++){var L=y[T],x=tc(L),k=tx(L)===eX,O=["top",eq].indexOf(x)>=0,D=O?"width":"height",S=tq(i,{placement:L,boundary:d,rootBoundary:u,altBoundary:f,padding:h}),N=O?k?eF:eU:k?eq:"top";E[D]>$[D]&&(N=tI(N));var I=tI(N),_=[];if(o&&_.push(S[x]<=0),l&&_.push(S[N]<=0,S[I]<=0),_.every(function(e){return e})){C=L,A=!1;break}w.set(L,_)}if(A)for(var P=g?3:1,M=function e(t){var i=y.find(function(e){var i=w.get(e);if(i)return i.slice(0,t).every(function(e){return e})});if(i)return C=i,"break"},H=P;H>0&&"break"!==M(H);H--);i.placement!==C&&(i.modifiersData[s]._skip=!0,i.placement=C,i.reset=!0)}},requiresIfExists:["offset"],data:{_skip:!1}};function tU(e,t,i){return void 0===i&&(i={x:0,y:0}),{top:e.top-t.height-i.y,right:e.right-t.width+i.x,bottom:e.bottom-t.height+i.y,left:e.left-t.width-i.x}}function tV(e){return["top",eF,eq,eU].some(function(t){return e[t]>=0})}let tK={name:"hide",enabled:!0,phase:"main",requiresIfExists:["preventOverflow"],fn:function e(t){var i=t.state,n=t.name,s=i.rects.reference,r=i.rects.popper,o=i.modifiersData.preventOverflow,a=tq(i,{elementContext:"reference"}),l=tq(i,{altBoundary:!0}),c=tU(a,s),h=tU(l,r,o),d=tV(c),u=tV(h);i.modifiersData[n]={referenceClippingOffsets:c,popperEscapeOffsets:h,isReferenceHidden:d,hasPopperEscaped:u},i.attributes.popper=Object.assign({},i.attributes.popper,{"data-popper-reference-hidden":d,"data-popper-escaped":u})}},tX={name:"offset",enabled:!0,phase:"main",requires:["popperOffsets"],fn:function e(t){var i=t.state,n=t.options,s=t.name,r=n.offset,o=void 0===r?[0,0]:r,a=e2.reduce(function(e,t){var n,s,r,a,l,c,h,d;return e[t]=(n=t,s=i.rects,r=o,l=[eU,"top"].indexOf(a=tc(n))>=0?-1:1,h=(c="function"==typeof r?r(Object.assign({},s,{placement:n})):r)[0],d=c[1],h=h||0,d=(d||0)*l,[eU,eF].indexOf(a)>=0?{x:d,y:h}:{x:h,y:d}),e},{}),l=a[i.placement],c=l.x,h=l.y;null!=i.modifiersData.popperOffsets&&(i.modifiersData.popperOffsets.x+=c,i.modifiersData.popperOffsets.y+=h),i.modifiersData[s]=a}},tY={name:"popperOffsets",enabled:!0,phase:"read",fn:function e(t){var i=t.state,n=t.name;i.modifiersData[n]=tz({reference:i.rects.reference,element:i.rects.popper,strategy:"absolute",placement:i.placement})},data:{}},t0={name:"preventOverflow",enabled:!0,phase:"main",fn:function e(t){var i,n=t.state,s=t.options,r=t.name,o=s.mainAxis,a=s.altAxis,l=s.boundary,c=s.rootBoundary,h=s.altBoundary,d=s.padding,u=s.tether,f=void 0===u||u,p=s.tetherOffset,g=void 0===p?0:p,m=tq(n,{boundary:l,rootBoundary:c,padding:d,altBoundary:h}),v=tc(n.placement),b=tx(n.placement),y=!b,E=t$(v),$="x"===(i=E)?"y":"x",w=n.modifiersData.popperOffsets,A=n.rects.reference,C=n.rects.popper,T="function"==typeof g?g(Object.assign({},n.rects,{placement:n.placement})):g,L="number"==typeof T?{mainAxis:T,altAxis:T}:Object.assign({mainAxis:0,altAxis:0},T),x=n.modifiersData.offset?n.modifiersData.offset[n.placement]:null,k={x:0,y:0};if(w){if(void 0===o||o){var O,D="y"===E?"top":eU,S="y"===E?eq:eF,N="y"===E?"height":"width",I=w[E],_=I+m[D],P=I-m[S],M=f?-C[N]/2:0,H=b===eX?A[N]:C[N],B=b===eX?-C[N]:-A[N],j=n.elements.arrow,W=f&&j?tp(j):{width:0,height:0},R=n.modifiersData["arrow#persistent"]?n.modifiersData["arrow#persistent"].padding:tA(),z=R[D],q=R[S],F=tw(0,A[N],W[N]),U=y?A[N]/2-M-F-z-L.mainAxis:H-F-z-L.mainAxis,V=y?-A[N]/2+M+F+q+L.mainAxis:B+F+q+L.mainAxis,K=n.elements.arrow&&tE(n.elements.arrow),X=K?"y"===E?K.clientTop||0:K.clientLeft||0:0,Y=null!=(O=null==x?void 0:x[E])?O:0,Q=tw(f?td(_,I+U-Y-X):_,I,f?th(P,I+V-Y):P);w[E]=Q,k[E]=Q-I}if(void 0!==a&&a){var G,Z,J,ee,et,ei=w[$],en="y"===$?"height":"width",es=ei+m["x"===E?"top":eU],er=ei-m["x"===E?eq:eF],eo=-1!==["top",eU].indexOf(v),ea=null!=(G=null==x?void 0:x[$])?G:0,el=eo?es:ei-A[en]-C[en]-ea+L.altAxis,ec=eo?ei+A[en]+C[en]-ea-L.altAxis:er,eh=f&&eo?(Z=el,J=ei,ee=ec,(et=tw(Z,J,ee))>ee?ee:et):tw(f?el:es,ei,f?ec:er);w[$]=eh,k[$]=eh-ei}n.modifiersData[r]=k}},requiresIfExists:["offset"]};var tQ={placement:"bottom",modifiers:[],strategy:"absolute"};function t1(){for(var e=arguments.length,t=Array(e),i=0;i<e;i++)t[i]=arguments[i];return!t.some(function(e){return!(e&&"function"==typeof e.getBoundingClientRect)})}function t3(e){void 0===e&&(e={});var t=e,i=t.defaultModifiers,n=void 0===i?[]:i,s=t.defaultOptions,r=void 0===s?tQ:s;return function e(t,i,s){void 0===s&&(s=r);var o,a,l={placement:"bottom",orderedModifiers:[],options:Object.assign({},tQ,r),modifiersData:{},elements:{reference:t,popper:i},attributes:{},styles:{}},c=[],h=!1,d={state:l,setOptions:function e(s){var o,a,h,f,p,g,m,v,b="function"==typeof s?s(l.options):s;u(),l.options=Object.assign({},r,l.options,b),l.scrollParents={reference:tr(t)?tj(t):t.contextElement?tj(t.contextElement):[],popper:tj(i)};var y=(v=(f=h=Object.keys(a=(o=[].concat(n,l.options.modifiers)).reduce(function(e,t){var i=e[t.name];return e[t.name]=i?Object.assign({},i,t,{options:Object.assign({},i.options,t.options),data:Object.assign({},i.data,t.data)}):t,e},{})).map(function(e){return a[e]}),p=new Map,g=new Set,m=[],f.forEach(function(e){p.set(e.name,e)}),f.forEach(function(e){g.has(e.name)||function e(t){g.add(t.name),[].concat(t.requires||[],t.requiresIfExists||[]).forEach(function(t){if(!g.has(t)){var i=p.get(t);i&&e(i)}}),m.push(t)}(e)}),m),ti.reduce(function(e,t){return e.concat(v.filter(function(e){return e.phase===t}))},[]));return l.orderedModifiers=y.filter(function(e){return e.enabled}),l.orderedModifiers.forEach(function(e){var t=e.name,i=e.options,n=e.effect;if("function"==typeof n){var s=n({state:l,name:t,instance:d,options:void 0===i?{}:i}),r=function e(){};c.push(s||r)}}),d.update()},forceUpdate:function e(){if(!h){var t,i,n,s,r,o,a,c,u,f,p,g,m,v,b,y=l.elements,E=y.reference,$=y.popper;if(t1(E,$)){l.rects={reference:(n=E,s=tE($),r="fixed"===l.options.strategy,o=to(s),p=to(s)&&(u=tu((c=(a=s).getBoundingClientRect()).width)/a.offsetWidth||1,f=tu(c.height)/a.offsetHeight||1,1!==u||1!==f),g=tv(s),m=tf(n,p),v={scrollLeft:0,scrollTop:0},b={x:0,y:0},(o||!o&&!r)&&(("body"!==tn(s)||tB(g))&&(v=(t=s,t!==ts(t)&&to(t)?{scrollLeft:(i=t).scrollLeft,scrollTop:i.scrollTop}:tM(t))),to(s)?(b=tf(s,!0),b.x+=s.clientLeft,b.y+=s.clientTop):g&&(b.x=tH(g))),{x:m.left+v.scrollLeft-b.x,y:m.top+v.scrollTop-b.y,width:m.width,height:m.height}),popper:tp($)},l.reset=!1,l.placement=l.options.placement,l.orderedModifiers.forEach(function(e){return l.modifiersData[e.name]=Object.assign({},e.data)});for(var w=0;w<l.orderedModifiers.length;w++){if(!0===l.reset){l.reset=!1,w=-1;continue}var A=l.orderedModifiers[w],C=A.fn,T=A.options,L=void 0===T?{}:T,x=A.name;"function"==typeof C&&(l=C({state:l,options:L,name:x,instance:d})||l)}}}},update:(o=function(){return new Promise(function(e){d.forceUpdate(),e(l)})},function(){return a||(a=new Promise(function(e){Promise.resolve().then(function(){a=void 0,e(o())})})),a}),destroy:function e(){u(),h=!0}};if(!t1(t,i))return d;function u(){c.forEach(function(e){return e()}),c=[]}return d.setOptions(s).then(function(e){!h&&s.onFirstUpdate&&s.onFirstUpdate(e)}),d}}var t2=t3(),t4=t3({defaultModifiers:[tS,tY,t9,tl]}),tG=t3({defaultModifiers:[tS,tY,t9,tl,tX,tF,t0,tL,tK]});let tZ=Object.freeze({__proto__:null,popperGenerator:t3,detectOverflow:tq,createPopperBase:t2,createPopper:tG,createPopperLite:t4,top:"top",bottom:eq,right:eF,left:eU,auto:eV,basePlacements:eK,start:eX,end:"end",clippingParents:eY,viewport:e0,popper:eQ,reference:e1,variationPlacements:e3,placements:e2,beforeRead:e4,read:eG,afterRead:eZ,beforeMain:e6,main:eJ,afterMain:e5,beforeWrite:e7,write:te,afterWrite:tt,modifierPhases:ti,applyStyles:tl,arrow:tL,computeStyles:t9,eventListeners:tS,flip:tF,hide:tK,offset:tX,popperOffsets:tY,preventOverflow:t0}),t6="dropdown",tJ=".bs.dropdown",t5=".data-api",t7="Escape",ie="Space",it="ArrowUp",ii="ArrowDown",is=RegExp(`${it}|${ii}|${t7}`),ir=`hide${tJ}`,io=`hidden${tJ}`,ia=`show${tJ}`,il=`shown${tJ}`,ic=`click${tJ}${t5}`,ih=`keydown${tJ}${t5}`,id=`keyup${tJ}${t5}`,iu="show",ip='[data-bs-toggle="dropdown"]',ig=".dropdown-menu",im=y()?"top-end":"top-start",i8=y()?"top-start":"top-end",iv=y()?"bottom-end":"bottom-start",ib=y()?"bottom-start":"bottom-end",iy=y()?"left-start":"right-start",iE=y()?"right-start":"left-start",i$={offset:[0,2],boundary:"clippingParents",reference:"toggle",display:"dynamic",popperConfig:null,autoClose:!0},iw={offset:"(array|string|function)",boundary:"(string|element)",reference:"(string|element|object)",display:"string",popperConfig:"(null|object|function)",autoClose:"(boolean|string)"};class iA extends z{constructor(e,t){super(e),this._popper=null,this._config=this._getConfig(t),this._menu=this._getMenuElement(),this._inNavbar=this._detectNavbar()}static get Default(){return i$}static get DefaultType(){return iw}static get NAME(){return t6}toggle(){return this._isShown()?this.hide():this.show()}show(){if(u(this._element)||this._isShown(this._menu))return;let e={relatedTarget:this._element},t=j.trigger(this._element,ia,e);if(t.defaultPrevented)return;let i=iA.getParentFromElement(this._element);this._inNavbar?J.setDataAttribute(this._menu,"popper","none"):this._createPopper(i),"ontouchstart"in document.documentElement&&!i.closest(".navbar-nav")&&[].concat(...document.body.children).forEach(e=>j.on(e,"mouseover",p)),this._element.focus(),this._element.setAttribute("aria-expanded",!0),this._menu.classList.add(iu),this._element.classList.add(iu),j.trigger(this._element,il,e)}hide(){if(u(this._element)||!this._isShown(this._menu))return;let e={relatedTarget:this._element};this._completeHide(e)}dispose(){this._popper&&this._popper.destroy(),super.dispose()}update(){this._inNavbar=this._detectNavbar(),this._popper&&this._popper.update()}_completeHide(e){let t=j.trigger(this._element,ir,e);!t.defaultPrevented&&("ontouchstart"in document.documentElement&&[].concat(...document.body.children).forEach(e=>j.off(e,"mouseover",p)),this._popper&&this._popper.destroy(),this._menu.classList.remove(iu),this._element.classList.remove(iu),this._element.setAttribute("aria-expanded","false"),J.removeDataAttribute(this._menu,"popper"),j.trigger(this._element,io,e))}_getConfig(e){if(h(t6,e={...this.constructor.Default,...J.getDataAttributes(this._element),...e},this.constructor.DefaultType),"object"==typeof e.reference&&!l(e.reference)&&"function"!=typeof e.reference.getBoundingClientRect)throw TypeError(`${t6.toUpperCase()}: Option "reference" provided type "object" without a required "getBoundingClientRect" method.`);return e}_createPopper(e){if(void 0===tZ)throw TypeError("Bootstrap's dropdowns require Popper (https://popper.js.org)");let t=this._element;"parent"===this._config.reference?t=e:l(this._config.reference)?t=c(this._config.reference):"object"==typeof this._config.reference&&(t=this._config.reference);let i=this._getPopperConfig(),n=i.modifiers.find(e=>"applyStyles"===e.name&&!1===e.enabled);this._popper=tG(t,this._menu,i),n&&J.setDataAttribute(this._menu,"popper","static")}_isShown(e=this._element){return e.classList.contains(iu)}_getMenuElement(){return ee.next(this._element,ig)[0]}_getPlacement(){let e=this._element.parentNode;if(e.classList.contains("dropend"))return iy;if(e.classList.contains("dropstart"))return iE;let t="end"===getComputedStyle(this._menu).getPropertyValue("--bs-position").trim();return e.classList.contains("dropup")?t?i8:im:t?ib:iv}_detectNavbar(){return null!==this._element.closest(".navbar")}_getOffset(){let{offset:e}=this._config;return"string"==typeof e?e.split(",").map(e=>Number.parseInt(e,10)):"function"==typeof e?t=>e(t,this._element):e}_getPopperConfig(){let e={placement:this._getPlacement(),modifiers:[{name:"preventOverflow",options:{boundary:this._config.boundary}},{name:"offset",options:{offset:this._getOffset()}}]};return"static"===this._config.display&&(e.modifiers=[{name:"applyStyles",enabled:!1}]),{...e,..."function"==typeof this._config.popperConfig?this._config.popperConfig(e):this._config.popperConfig}}_selectMenuItem({key:e,target:t}){let i=ee.find(".dropdown-menu .dropdown-item:not(.disabled):not(:disabled)",this._menu).filter(d);i.length&&A(i,t,e===ii,!i.includes(t)).focus()}static jQueryInterface(e){return this.each(function(){let t=iA.getOrCreateInstance(this,e);if("string"==typeof e){if(void 0===t[e])throw TypeError(`No method named "${e}"`);t[e]()}})}static clearMenus(e){if(e&&(2===e.button||"keyup"===e.type&&"Tab"!==e.key))return;let t=ee.find(ip);for(let i=0,n=t.length;i<n;i++){let s=iA.getInstance(t[i]);if(!s||!1===s._config.autoClose||!s._isShown())continue;let r={relatedTarget:s._element};if(e){let o=e.composedPath(),a=o.includes(s._menu);if(o.includes(s._element)||"inside"===s._config.autoClose&&!a||"outside"===s._config.autoClose&&a||s._menu.contains(e.target)&&("keyup"===e.type&&"Tab"===e.key||/input|select|option|textarea|form/i.test(e.target.tagName)))continue;"click"===e.type&&(r.clickEvent=e)}s._completeHide(r)}}static getParentFromElement(e){return r(e)||e.parentNode}static dataApiKeydownHandler(e){if(/input|textarea/i.test(e.target.tagName)?e.key===ie||e.key!==t7&&(e.key!==ii&&e.key!==it||e.target.closest(ig)):!is.test(e.key))return;let t=this.classList.contains(iu);if(!t&&e.key===t7||(e.preventDefault(),e.stopPropagation(),u(this)))return;let i=this.matches(ip)?this:ee.prev(this,ip)[0],n=iA.getOrCreateInstance(i);if(e.key===t7){n.hide();return}if(e.key===it||e.key===ii){t||n.show(),n._selectMenuItem(e);return}t&&e.key!==ie||iA.clearMenus()}}j.on(document,ih,ip,iA.dataApiKeydownHandler),j.on(document,ih,ig,iA.dataApiKeydownHandler),j.on(document,ic,iA.clearMenus),j.on(document,id,iA.clearMenus),j.on(document,ic,ip,function(e){e.preventDefault(),iA.getOrCreateInstance(this).toggle()}),E(iA);let iC=".fixed-top, .fixed-bottom, .is-fixed, .sticky-top",iT=".sticky-top";class iL{constructor(){this._element=document.body}getWidth(){let e=document.documentElement.clientWidth;return Math.abs(window.innerWidth-e)}hide(){let e=this.getWidth();this._disableOverFlow(),this._setElementAttributes(this._element,"paddingRight",t=>t+e),this._setElementAttributes(iC,"paddingRight",t=>t+e),this._setElementAttributes(iT,"marginRight",t=>t-e)}_disableOverFlow(){this._saveInitialAttribute(this._element,"overflow"),this._element.style.overflow="hidden"}_setElementAttributes(e,t,i){let n=this.getWidth(),s=e=>{if(e!==this._element&&window.innerWidth>e.clientWidth+n)return;this._saveInitialAttribute(e,t);let s=window.getComputedStyle(e)[t];e.style[t]=`${i(Number.parseFloat(s))}px`};this._applyManipulationCallback(e,s)}reset(){this._resetElementAttributes(this._element,"overflow"),this._resetElementAttributes(this._element,"paddingRight"),this._resetElementAttributes(iC,"paddingRight"),this._resetElementAttributes(iT,"marginRight")}_saveInitialAttribute(e,t){let i=e.style[t];i&&J.setDataAttribute(e,t,i)}_resetElementAttributes(e,t){let i=e=>{let i=J.getDataAttribute(e,t);void 0===i?e.style.removeProperty(t):(J.removeDataAttribute(e,t),e.style[t]=i)};this._applyManipulationCallback(e,i)}_applyManipulationCallback(e,t){l(e)?t(e):ee.find(e,this._element).forEach(t)}isOverflowing(){return this.getWidth()>0}}let ix={className:"modal-backdrop",isVisible:!0,isAnimated:!1,rootElement:"body",clickCallback:null},ik={className:"string",isVisible:"boolean",isAnimated:"boolean",rootElement:"(element|string)",clickCallback:"(function|null)"},iO="backdrop",i9="show",iD=`mousedown.bs.${iO}`;class iS{constructor(e){this._config=this._getConfig(e),this._isAppended=!1,this._element=null}show(e){if(!this._config.isVisible){$(e);return}this._append(),this._config.isAnimated&&g(this._getElement()),this._getElement().classList.add(i9),this._emulateAnimation(()=>{$(e)})}hide(e){if(!this._config.isVisible){$(e);return}this._getElement().classList.remove(i9),this._emulateAnimation(()=>{this.dispose(),$(e)})}_getElement(){if(!this._element){let e=document.createElement("div");e.className=this._config.className,this._config.isAnimated&&e.classList.add("fade"),this._element=e}return this._element}_getConfig(e){return(e={...ix,..."object"==typeof e?e:{}}).rootElement=c(e.rootElement),h(iO,e,ik),e}_append(){!this._isAppended&&(this._config.rootElement.append(this._getElement()),j.on(this._getElement(),iD,()=>{$(this._config.clickCallback)}),this._isAppended=!0)}dispose(){this._isAppended&&(j.off(this._element,iD),this._element.remove(),this._isAppended=!1)}_emulateAnimation(e){w(e,this._getElement(),this._config.isAnimated)}}let iN={trapElement:null,autofocus:!0},iI={trapElement:"element",autofocus:"boolean"},i_=".bs.focustrap",iP=`focusin${i_}`,iM=`keydown.tab${i_}`,iH="backward";class iB{constructor(e){this._config=this._getConfig(e),this._isActive=!1,this._lastTabNavDirection=null}activate(){let{trapElement:e,autofocus:t}=this._config;!this._isActive&&(t&&e.focus(),j.off(document,i_),j.on(document,iP,e=>this._handleFocusin(e)),j.on(document,iM,e=>this._handleKeydown(e)),this._isActive=!0)}deactivate(){this._isActive&&(this._isActive=!1,j.off(document,i_))}_handleFocusin(e){let{target:t}=e,{trapElement:i}=this._config;if(t===document||t===i||i.contains(t))return;let n=ee.focusableChildren(i);0===n.length?i.focus():this._lastTabNavDirection===iH?n[n.length-1].focus():n[0].focus()}_handleKeydown(e){"Tab"===e.key&&(this._lastTabNavDirection=e.shiftKey?iH:"forward")}_getConfig(e){return h("focustrap",e={...iN,..."object"==typeof e?e:{}},iI),e}}let ij="modal",iW=".bs.modal",iR="Escape",iz={backdrop:!0,keyboard:!0,focus:!0},iq={backdrop:"(boolean|string)",keyboard:"boolean",focus:"boolean"},iF=`hide${iW}`,iU=`hidePrevented${iW}`,iV=`hidden${iW}`,iK=`show${iW}`,iX=`shown${iW}`,iY=`resize${iW}`,i0=`click.dismiss${iW}`,iQ=`keydown.dismiss${iW}`,i1=`mouseup.dismiss${iW}`,i3=`mousedown.dismiss${iW}`,i2=`click${iW}.data-api`,i4="modal-open",iG="show",iZ="modal-static";class i6 extends z{constructor(e,t){super(e),this._config=this._getConfig(t),this._dialog=ee.findOne(".modal-dialog",this._element),this._backdrop=this._initializeBackDrop(),this._focustrap=this._initializeFocusTrap(),this._isShown=!1,this._ignoreBackdropClick=!1,this._isTransitioning=!1,this._scrollBar=new iL}static get Default(){return iz}static get NAME(){return ij}toggle(e){return this._isShown?this.hide():this.show(e)}show(e){if(this._isShown||this._isTransitioning)return;let t=j.trigger(this._element,iK,{relatedTarget:e});!t.defaultPrevented&&(this._isShown=!0,this._isAnimated()&&(this._isTransitioning=!0),this._scrollBar.hide(),document.body.classList.add(i4),this._adjustDialog(),this._setEscapeEvent(),this._setResizeEvent(),j.on(this._dialog,i3,()=>{j.one(this._element,i1,e=>{e.target===this._element&&(this._ignoreBackdropClick=!0)})}),this._showBackdrop(()=>this._showElement(e)))}hide(){if(!this._isShown||this._isTransitioning)return;let e=j.trigger(this._element,iF);if(e.defaultPrevented)return;this._isShown=!1;let t=this._isAnimated();t&&(this._isTransitioning=!0),this._setEscapeEvent(),this._setResizeEvent(),this._focustrap.deactivate(),this._element.classList.remove(iG),j.off(this._element,i0),j.off(this._dialog,i3),this._queueCallback(()=>this._hideModal(),this._element,t)}dispose(){[window,this._dialog].forEach(e=>j.off(e,iW)),this._backdrop.dispose(),this._focustrap.deactivate(),super.dispose()}handleUpdate(){this._adjustDialog()}_initializeBackDrop(){return new iS({isVisible:Boolean(this._config.backdrop),isAnimated:this._isAnimated()})}_initializeFocusTrap(){return new iB({trapElement:this._element})}_getConfig(e){return h(ij,e={...iz,...J.getDataAttributes(this._element),..."object"==typeof e?e:{}},iq),e}_showElement(e){let t=this._isAnimated(),i=ee.findOne(".modal-body",this._dialog);this._element.parentNode&&this._element.parentNode.nodeType===Node.ELEMENT_NODE||document.body.append(this._element),this._element.style.display="block",this._element.removeAttribute("aria-hidden"),this._element.setAttribute("aria-modal",!0),this._element.setAttribute("role","dialog"),this._element.scrollTop=0,i&&(i.scrollTop=0),t&&g(this._element),this._element.classList.add(iG);let n=()=>{this._config.focus&&this._focustrap.activate(),this._isTransitioning=!1,j.trigger(this._element,iX,{relatedTarget:e})};this._queueCallback(n,this._dialog,t)}_setEscapeEvent(){this._isShown?j.on(this._element,iQ,e=>{this._config.keyboard&&e.key===iR?(e.preventDefault(),this.hide()):this._config.keyboard||e.key!==iR||this._triggerBackdropTransition()}):j.off(this._element,iQ)}_setResizeEvent(){this._isShown?j.on(window,iY,()=>this._adjustDialog()):j.off(window,iY)}_hideModal(){this._element.style.display="none",this._element.setAttribute("aria-hidden",!0),this._element.removeAttribute("aria-modal"),this._element.removeAttribute("role"),this._isTransitioning=!1,this._backdrop.hide(()=>{document.body.classList.remove(i4),this._resetAdjustments(),this._scrollBar.reset(),j.trigger(this._element,iV)})}_showBackdrop(e){j.on(this._element,i0,e=>{if(this._ignoreBackdropClick){this._ignoreBackdropClick=!1;return}e.target===e.currentTarget&&(!0===this._config.backdrop?this.hide():"static"===this._config.backdrop&&this._triggerBackdropTransition())}),this._backdrop.show(e)}_isAnimated(){return this._element.classList.contains("fade")}_triggerBackdropTransition(){let e=j.trigger(this._element,iU);if(e.defaultPrevented)return;let{classList:t,scrollHeight:i,style:n}=this._element,s=i>document.documentElement.clientHeight;!(!s&&"hidden"===n.overflowY||t.contains(iZ))&&(s||(n.overflowY="hidden"),t.add(iZ),this._queueCallback(()=>{t.remove(iZ),s||this._queueCallback(()=>{n.overflowY=""},this._dialog)},this._dialog),this._element.focus())}_adjustDialog(){let e=this._element.scrollHeight>document.documentElement.clientHeight,t=this._scrollBar.getWidth(),i=t>0;(!i&&e&&!y()||i&&!e&&y())&&(this._element.style.paddingLeft=`${t}px`),(i&&!e&&!y()||!i&&e&&y())&&(this._element.style.paddingRight=`${t}px`)}_resetAdjustments(){this._element.style.paddingLeft="",this._element.style.paddingRight=""}static jQueryInterface(e,t){return this.each(function(){let i=i6.getOrCreateInstance(this,e);if("string"==typeof e){if(void 0===i[e])throw TypeError(`No method named "${e}"`);i[e](t)}})}}j.on(document,i2,'[data-bs-toggle="modal"]',function(e){let t=r(this);["A","AREA"].includes(this.tagName)&&e.preventDefault(),j.one(t,iK,e=>{!e.defaultPrevented&&j.one(t,iV,()=>{d(this)&&this.focus()})});let i=ee.findOne(".modal.show");i&&i6.getInstance(i).hide();let n=i6.getOrCreateInstance(t);n.toggle(this)}),q(i6),E(i6);let iJ="offcanvas",i5=".bs.offcanvas",i7=".data-api",ne=`load${i5}${i7}`,nt={backdrop:!0,keyboard:!0,scroll:!1},ni={backdrop:"boolean",keyboard:"boolean",scroll:"boolean"},nn="show",ns=".offcanvas.show",nr=`show${i5}`,no=`shown${i5}`,na=`hide${i5}`,nl=`hidden${i5}`,nc=`click${i5}${i7}`,nh=`keydown.dismiss${i5}`;class nd extends z{constructor(e,t){super(e),this._config=this._getConfig(t),this._isShown=!1,this._backdrop=this._initializeBackDrop(),this._focustrap=this._initializeFocusTrap(),this._addEventListeners()}static get NAME(){return iJ}static get Default(){return nt}toggle(e){return this._isShown?this.hide():this.show(e)}show(e){if(this._isShown)return;let t=j.trigger(this._element,nr,{relatedTarget:e});if(t.defaultPrevented)return;this._isShown=!0,this._element.style.visibility="visible",this._backdrop.show(),this._config.scroll||new iL().hide(),this._element.removeAttribute("aria-hidden"),this._element.setAttribute("aria-modal",!0),this._element.setAttribute("role","dialog"),this._element.classList.add(nn);let i=()=>{this._config.scroll||this._focustrap.activate(),j.trigger(this._element,no,{relatedTarget:e})};this._queueCallback(i,this._element,!0)}hide(){if(!this._isShown)return;let e=j.trigger(this._element,na);if(e.defaultPrevented)return;this._focustrap.deactivate(),this._element.blur(),this._isShown=!1,this._element.classList.remove(nn),this._backdrop.hide();let t=()=>{this._element.setAttribute("aria-hidden",!0),this._element.removeAttribute("aria-modal"),this._element.removeAttribute("role"),this._element.style.visibility="hidden",this._config.scroll||new iL().reset(),j.trigger(this._element,nl)};this._queueCallback(t,this._element,!0)}dispose(){this._backdrop.dispose(),this._focustrap.deactivate(),super.dispose()}_getConfig(e){return h(iJ,e={...nt,...J.getDataAttributes(this._element),..."object"==typeof e?e:{}},ni),e}_initializeBackDrop(){return new iS({className:"offcanvas-backdrop",isVisible:this._config.backdrop,isAnimated:!0,rootElement:this._element.parentNode,clickCallback:()=>this.hide()})}_initializeFocusTrap(){return new iB({trapElement:this._element})}_addEventListeners(){j.on(this._element,nh,e=>{this._config.keyboard&&"Escape"===e.key&&this.hide()})}static jQueryInterface(e){return this.each(function(){let t=nd.getOrCreateInstance(this,e);if("string"==typeof e){if(void 0===t[e]||e.startsWith("_")||"constructor"===e)throw TypeError(`No method named "${e}"`);t[e](this)}})}}j.on(document,nc,'[data-bs-toggle="offcanvas"]',function(e){let t=r(this);if(["A","AREA"].includes(this.tagName)&&e.preventDefault(),u(this))return;j.one(t,nl,()=>{d(this)&&this.focus()});let i=ee.findOne(ns);i&&i!==t&&nd.getInstance(i).hide();let n=nd.getOrCreateInstance(t);n.toggle(this)}),j.on(window,ne,()=>ee.find(ns).forEach(e=>nd.getOrCreateInstance(e).show())),q(nd),E(nd);let nu=new Set(["background","cite","href","itemtype","longdesc","poster","src","xlink:href"]),nf=/^(?:(?:https?|mailto|ftp|tel|file|sms):|[^#&/:?]*(?:[#/?]|$))/i,np=/^data:(?:image\/(?:bmp|gif|jpeg|jpg|png|tiff|webp)|video\/(?:mpeg|mp4|ogg|webm)|audio\/(?:mp3|oga|ogg|opus));base64,[\d+/a-z]+=*$/i,ng=(e,t)=>{let i=e.nodeName.toLowerCase();if(t.includes(i))return!nu.has(i)||Boolean(nf.test(e.nodeValue)||np.test(e.nodeValue));let n=t.filter(e=>e instanceof RegExp);for(let s=0,r=n.length;s<r;s++)if(n[s].test(i))return!0;return!1};function nm(e,t,i){if(!e.length)return e;if(i&&"function"==typeof i)return i(e);let n=new window.DOMParser,s=n.parseFromString(e,"text/html"),r=[].concat(...s.body.querySelectorAll("*"));for(let o=0,a=r.length;o<a;o++){let l=r[o],c=l.nodeName.toLowerCase();if(!Object.keys(t).includes(c)){l.remove();continue}let h=[].concat(...l.attributes),d=[].concat(t["*"]||[],t[c]||[]);h.forEach(e=>{ng(e,d)||l.removeAttribute(e.nodeName)})}return s.body.innerHTML}let n8="tooltip",nv=".bs.tooltip",nb=new Set(["sanitize","allowList","sanitizeFn"]),ny={animation:"boolean",template:"string",title:"(string|element|function)",trigger:"string",delay:"(number|object)",html:"boolean",selector:"(string|boolean)",placement:"(string|function)",offset:"(array|string|function)",container:"(string|element|boolean)",fallbackPlacements:"array",boundary:"(string|element)",customClass:"(string|function)",sanitize:"boolean",sanitizeFn:"(null|function)",allowList:"object",popperConfig:"(null|object|function)"},nE={AUTO:"auto",TOP:"top",RIGHT:y()?"left":"right",BOTTOM:"bottom",LEFT:y()?"right":"left"},n$={animation:!0,template:'<div class="tooltip" role="tooltip"><div class="tooltip-arrow"></div><div class="tooltip-inner"></div></div>',trigger:"hover focus",title:"",delay:0,html:!1,selector:!1,placement:"top",offset:[0,0],container:!1,fallbackPlacements:["top","right","bottom","left"],boundary:"clippingParents",customClass:"",sanitize:!0,sanitizeFn:null,allowList:{"*":["class","dir","id","lang","role",/^aria-[\w-]*$/i],a:["target","href","title","rel"],area:[],b:[],br:[],col:[],code:[],div:[],em:[],hr:[],h1:[],h2:[],h3:[],h4:[],h5:[],h6:[],i:[],img:["src","srcset","alt","title","width","height"],li:[],ol:[],p:[],pre:[],s:[],small:[],span:[],sub:[],sup:[],strong:[],u:[],ul:[]},popperConfig:null},nw={HIDE:`hide${nv}`,HIDDEN:`hidden${nv}`,SHOW:`show${nv}`,SHOWN:`shown${nv}`,INSERTED:`inserted${nv}`,CLICK:`click${nv}`,FOCUSIN:`focusin${nv}`,FOCUSOUT:`focusout${nv}`,MOUSEENTER:`mouseenter${nv}`,MOUSELEAVE:`mouseleave${nv}`},nA="fade",nC="show",nT="show",nL=".tooltip-inner",nx=".modal",nk="hide.bs.modal",nO="hover",n9="focus";class nD extends z{constructor(e,t){if(void 0===tZ)throw TypeError("Bootstrap's tooltips require Popper (https://popper.js.org)");super(e),this._isEnabled=!0,this._timeout=0,this._hoverState="",this._activeTrigger={},this._popper=null,this._config=this._getConfig(t),this.tip=null,this._setListeners()}static get Default(){return n$}static get NAME(){return n8}static get Event(){return nw}static get DefaultType(){return ny}enable(){this._isEnabled=!0}disable(){this._isEnabled=!1}toggleEnabled(){this._isEnabled=!this._isEnabled}toggle(e){if(this._isEnabled){if(e){let t=this._initializeOnDelegatedTarget(e);t._activeTrigger.click=!t._activeTrigger.click,t._isWithActiveTrigger()?t._enter(null,t):t._leave(null,t)}else{if(this.getTipElement().classList.contains(nC)){this._leave(null,this);return}this._enter(null,this)}}}dispose(){clearTimeout(this._timeout),j.off(this._element.closest(nx),nk,this._hideModalHandler),this.tip&&this.tip.remove(),this._disposePopper(),super.dispose()}show(){if("none"===this._element.style.display)throw Error("Please use show on visible elements");if(!(this.isWithContent()&&this._isEnabled))return;let e=j.trigger(this._element,this.constructor.Event.SHOW),t=f(this._element),n=null===t?this._element.ownerDocument.documentElement.contains(this._element):t.contains(this._element);if(e.defaultPrevented||!n)return;"tooltip"===this.constructor.NAME&&this.tip&&this.getTitle()!==this.tip.querySelector(nL).innerHTML&&(this._disposePopper(),this.tip.remove(),this.tip=null);let s=this.getTipElement(),r=i(this.constructor.NAME);s.setAttribute("id",r),this._element.setAttribute("aria-describedby",r),this._config.animation&&s.classList.add(nA);let o="function"==typeof this._config.placement?this._config.placement.call(this,s,this._element):this._config.placement,a=this._getAttachment(o);this._addAttachmentClass(a);let{container:l}=this._config;R.set(s,this.constructor.DATA_KEY,this),this._element.ownerDocument.documentElement.contains(this.tip)||(l.append(s),j.trigger(this._element,this.constructor.Event.INSERTED)),this._popper?this._popper.update():this._popper=tG(this._element,s,this._getPopperConfig(a)),s.classList.add(nC);let c=this._resolvePossibleFunction(this._config.customClass);c&&s.classList.add(...c.split(" ")),"ontouchstart"in document.documentElement&&[].concat(...document.body.children).forEach(e=>{j.on(e,"mouseover",p)});let h=()=>{let e=this._hoverState;this._hoverState=null,j.trigger(this._element,this.constructor.Event.SHOWN),"out"===e&&this._leave(null,this)},d=this.tip.classList.contains(nA);this._queueCallback(h,this.tip,d)}hide(){if(!this._popper)return;let e=this.getTipElement(),t=()=>{!this._isWithActiveTrigger()&&(this._hoverState!==nT&&e.remove(),this._cleanTipClass(),this._element.removeAttribute("aria-describedby"),j.trigger(this._element,this.constructor.Event.HIDDEN),this._disposePopper())},i=j.trigger(this._element,this.constructor.Event.HIDE);if(i.defaultPrevented)return;e.classList.remove(nC),"ontouchstart"in document.documentElement&&[].concat(...document.body.children).forEach(e=>j.off(e,"mouseover",p)),this._activeTrigger.click=!1,this._activeTrigger[n9]=!1,this._activeTrigger[nO]=!1;let n=this.tip.classList.contains(nA);this._queueCallback(t,this.tip,n),this._hoverState=""}update(){null!==this._popper&&this._popper.update()}isWithContent(){return Boolean(this.getTitle())}getTipElement(){if(this.tip)return this.tip;let e=document.createElement("div");e.innerHTML=this._config.template;let t=e.children[0];return this.setContent(t),t.classList.remove(nA,nC),this.tip=t,this.tip}setContent(e){this._sanitizeAndSetContent(e,this.getTitle(),nL)}_sanitizeAndSetContent(e,t,i){let n=ee.findOne(i,e);if(!t&&n){n.remove();return}this.setElementContent(n,t)}setElementContent(e,t){if(null!==e){if(l(t)){t=c(t),this._config.html?t.parentNode!==e&&(e.innerHTML="",e.append(t)):e.textContent=t.textContent;return}this._config.html?(this._config.sanitize&&(t=nm(t,this._config.allowList,this._config.sanitizeFn)),e.innerHTML=t):e.textContent=t}}getTitle(){let e=this._element.getAttribute("data-bs-original-title")||this._config.title;return this._resolvePossibleFunction(e)}updateAttachment(e){return"right"===e?"end":"left"===e?"start":e}_initializeOnDelegatedTarget(e,t){return t||this.constructor.getOrCreateInstance(e.delegateTarget,this._getDelegateConfig())}_getOffset(){let{offset:e}=this._config;return"string"==typeof e?e.split(",").map(e=>Number.parseInt(e,10)):"function"==typeof e?t=>e(t,this._element):e}_resolvePossibleFunction(e){return"function"==typeof e?e.call(this._element):e}_getPopperConfig(e){let t={placement:e,modifiers:[{name:"flip",options:{fallbackPlacements:this._config.fallbackPlacements}},{name:"offset",options:{offset:this._getOffset()}},{name:"preventOverflow",options:{boundary:this._config.boundary}},{name:"arrow",options:{element:`.${this.constructor.NAME}-arrow`}},{name:"onChange",enabled:!0,phase:"afterWrite",fn:e=>this._handlePopperPlacementChange(e)}],onFirstUpdate:e=>{e.options.placement!==e.placement&&this._handlePopperPlacementChange(e)}};return{...t,..."function"==typeof this._config.popperConfig?this._config.popperConfig(t):this._config.popperConfig}}_addAttachmentClass(e){this.getTipElement().classList.add(`${this._getBasicClassPrefix()}-${this.updateAttachment(e)}`)}_getAttachment(e){return nE[e.toUpperCase()]}_setListeners(){let e=this._config.trigger.split(" ");e.forEach(e=>{if("click"===e)j.on(this._element,this.constructor.Event.CLICK,this._config.selector,e=>this.toggle(e));else if("manual"!==e){let t=e===nO?this.constructor.Event.MOUSEENTER:this.constructor.Event.FOCUSIN,i=e===nO?this.constructor.Event.MOUSELEAVE:this.constructor.Event.FOCUSOUT;j.on(this._element,t,this._config.selector,e=>this._enter(e)),j.on(this._element,i,this._config.selector,e=>this._leave(e))}}),this._hideModalHandler=()=>{this._element&&this.hide()},j.on(this._element.closest(nx),nk,this._hideModalHandler),this._config.selector?this._config={...this._config,trigger:"manual",selector:""}:this._fixTitle()}_fixTitle(){let e=this._element.getAttribute("title"),t=typeof this._element.getAttribute("data-bs-original-title");(e||"string"!==t)&&(this._element.setAttribute("data-bs-original-title",e||""),!e||this._element.getAttribute("aria-label")||this._element.textContent||this._element.setAttribute("aria-label",e),this._element.setAttribute("title",""))}_enter(e,t){if(t=this._initializeOnDelegatedTarget(e,t),e&&(t._activeTrigger["focusin"===e.type?n9:nO]=!0),t.getTipElement().classList.contains(nC)||t._hoverState===nT){t._hoverState=nT;return}if(clearTimeout(t._timeout),t._hoverState=nT,!t._config.delay||!t._config.delay.show){t.show();return}t._timeout=setTimeout(()=>{t._hoverState===nT&&t.show()},t._config.delay.show)}_leave(e,t){if(t=this._initializeOnDelegatedTarget(e,t),e&&(t._activeTrigger["focusout"===e.type?n9:nO]=t._element.contains(e.relatedTarget)),!t._isWithActiveTrigger()){if(clearTimeout(t._timeout),t._hoverState="out",!t._config.delay||!t._config.delay.hide){t.hide();return}t._timeout=setTimeout(()=>{"out"===t._hoverState&&t.hide()},t._config.delay.hide)}}_isWithActiveTrigger(){for(let e in this._activeTrigger)if(this._activeTrigger[e])return!0;return!1}_getConfig(e){let t=J.getDataAttributes(this._element);return Object.keys(t).forEach(e=>{nb.has(e)&&delete t[e]}),(e={...this.constructor.Default,...t,..."object"==typeof e&&e?e:{}}).container=!1===e.container?document.body:c(e.container),"number"==typeof e.delay&&(e.delay={show:e.delay,hide:e.delay}),"number"==typeof e.title&&(e.title=e.title.toString()),"number"==typeof e.content&&(e.content=e.content.toString()),h(n8,e,this.constructor.DefaultType),e.sanitize&&(e.template=nm(e.template,e.allowList,e.sanitizeFn)),e}_getDelegateConfig(){let e={};for(let t in this._config)this.constructor.Default[t]!==this._config[t]&&(e[t]=this._config[t]);return e}_cleanTipClass(){let e=this.getTipElement(),t=RegExp(`(^|\\s)${this._getBasicClassPrefix()}\\S+`,"g"),i=e.getAttribute("class").match(t);null!==i&&i.length>0&&i.map(e=>e.trim()).forEach(t=>e.classList.remove(t))}_getBasicClassPrefix(){return"bs-tooltip"}_handlePopperPlacementChange(e){let{state:t}=e;t&&(this.tip=t.elements.popper,this._cleanTipClass(),this._addAttachmentClass(this._getAttachment(t.placement)))}_disposePopper(){this._popper&&(this._popper.destroy(),this._popper=null)}static jQueryInterface(e){return this.each(function(){let t=nD.getOrCreateInstance(this,e);if("string"==typeof e){if(void 0===t[e])throw TypeError(`No method named "${e}"`);t[e]()}})}}E(nD);let nS=".bs.popover",nN={...nD.Default,placement:"right",offset:[0,8],trigger:"click",content:"",template:'<div class="popover" role="tooltip"><div class="popover-arrow"></div><h3 class="popover-header"></h3><div class="popover-body"></div></div>'},nI={...nD.DefaultType,content:"(string|element|function)"},n_={HIDE:`hide${nS}`,HIDDEN:`hidden${nS}`,SHOW:`show${nS}`,SHOWN:`shown${nS}`,INSERTED:`inserted${nS}`,CLICK:`click${nS}`,FOCUSIN:`focusin${nS}`,FOCUSOUT:`focusout${nS}`,MOUSEENTER:`mouseenter${nS}`,MOUSELEAVE:`mouseleave${nS}`};class nP extends nD{static get Default(){return nN}static get NAME(){return"popover"}static get Event(){return n_}static get DefaultType(){return nI}isWithContent(){return this.getTitle()||this._getContent()}setContent(e){this._sanitizeAndSetContent(e,this.getTitle(),".popover-header"),this._sanitizeAndSetContent(e,this._getContent(),".popover-body")}_getContent(){return this._resolvePossibleFunction(this._config.content)}_getBasicClassPrefix(){return"bs-popover"}static jQueryInterface(e){return this.each(function(){let t=nP.getOrCreateInstance(this,e);if("string"==typeof e){if(void 0===t[e])throw TypeError(`No method named "${e}"`);t[e]()}})}}E(nP);let nM="scrollspy",nH=".bs.scrollspy",nB={offset:10,method:"auto",target:""},nj={offset:"number",method:"string",target:"(string|element)"},nW=`activate${nH}`,nR=`scroll${nH}`,nz=`load${nH}.data-api`,nq="dropdown-item",nF="active",nU=".nav-link",nV=".list-group-item",nK=`${nU}, ${nV}, .${nq}`,nX="position";class nY extends z{constructor(e,t){super(e),this._scrollElement="BODY"===this._element.tagName?window:this._element,this._config=this._getConfig(t),this._offsets=[],this._targets=[],this._activeTarget=null,this._scrollHeight=0,j.on(this._scrollElement,nR,()=>this._process()),this.refresh(),this._process()}static get Default(){return nB}static get NAME(){return nM}refresh(){let e=this._scrollElement===this._scrollElement.window?"offset":nX,t="auto"===this._config.method?e:this._config.method,i=t===nX?this._getScrollTop():0;this._offsets=[],this._targets=[],this._scrollHeight=this._getScrollHeight();let n=ee.find(nK,this._config.target);n.map(e=>{let n=s(e),r=n?ee.findOne(n):null;if(r){let o=r.getBoundingClientRect();if(o.width||o.height)return[J[t](r).top+i,n]}return null}).filter(e=>e).sort((e,t)=>e[0]-t[0]).forEach(e=>{this._offsets.push(e[0]),this._targets.push(e[1])})}dispose(){j.off(this._scrollElement,nH),super.dispose()}_getConfig(e){return(e={...nB,...J.getDataAttributes(this._element),..."object"==typeof e&&e?e:{}}).target=c(e.target)||document.documentElement,h(nM,e,nj),e}_getScrollTop(){return this._scrollElement===window?this._scrollElement.pageYOffset:this._scrollElement.scrollTop}_getScrollHeight(){return this._scrollElement.scrollHeight||Math.max(document.body.scrollHeight,document.documentElement.scrollHeight)}_getOffsetHeight(){return this._scrollElement===window?window.innerHeight:this._scrollElement.getBoundingClientRect().height}_process(){let e=this._getScrollTop()+this._config.offset,t=this._getScrollHeight(),i=this._config.offset+t-this._getOffsetHeight();if(this._scrollHeight!==t&&this.refresh(),e>=i){let n=this._targets[this._targets.length-1];this._activeTarget!==n&&this._activate(n);return}if(this._activeTarget&&e<this._offsets[0]&&this._offsets[0]>0){this._activeTarget=null,this._clear();return}for(let s=this._offsets.length;s--;){let r=this._activeTarget!==this._targets[s]&&e>=this._offsets[s]&&(void 0===this._offsets[s+1]||e<this._offsets[s+1]);r&&this._activate(this._targets[s])}}_activate(e){this._activeTarget=e,this._clear();let t=nK.split(",").map(t=>`${t}[data-bs-target="${e}"],${t}[href="${e}"]`),i=ee.findOne(t.join(","),this._config.target);i.classList.add(nF),i.classList.contains(nq)?ee.findOne(".dropdown-toggle",i.closest(".dropdown")).classList.add(nF):ee.parents(i,".nav, .list-group").forEach(e=>{ee.prev(e,`${nU}, ${nV}`).forEach(e=>e.classList.add(nF)),ee.prev(e,".nav-item").forEach(e=>{ee.children(e,nU).forEach(e=>e.classList.add(nF))})}),j.trigger(this._scrollElement,nW,{relatedTarget:e})}_clear(){ee.find(nK,this._config.target).filter(e=>e.classList.contains(nF)).forEach(e=>e.classList.remove(nF))}static jQueryInterface(e){return this.each(function(){let t=nY.getOrCreateInstance(this,e);if("string"==typeof e){if(void 0===t[e])throw TypeError(`No method named "${e}"`);t[e]()}})}}j.on(window,nz,()=>{ee.find('[data-bs-spy="scroll"]').forEach(e=>new nY(e))}),E(nY);let n0=".bs.tab",nQ=".data-api",n1=`hide${n0}`,n3=`hidden${n0}`,n2=`show${n0}`,n4=`shown${n0}`,nG=`click${n0}${nQ}`,nZ=`mouseover${n0}${nQ}`,n6=`mouseleaveall${n0}${nQ}`,nJ="active",n5="fade",n7="show",se=".active",st=":scope > li > .active";class si extends z{static get NAME(){return"tab"}show(){if(this._element.parentNode&&this._element.parentNode.nodeType===Node.ELEMENT_NODE&&this._element.classList.contains(nJ))return;let e,t=r(this._element),i=this._element.closest(".nav, .list-group");if(i){let n="UL"===i.nodeName||"OL"===i.nodeName?st:se;e=(e=ee.find(n,i))[e.length-1]}let s=e?j.trigger(e,n1,{relatedTarget:this._element}):null,o=j.trigger(this._element,n2,{relatedTarget:e});if(o.defaultPrevented||null!==s&&s.defaultPrevented)return;this._activate(this._element,i);let a=()=>{j.trigger(e,n3,{relatedTarget:this._element}),j.trigger(this._element,n4,{relatedTarget:e})};t?this._activate(t,t.parentNode,a):a()}_activate(e,t,i){let n=t&&("UL"===t.nodeName||"OL"===t.nodeName)?ee.find(st,t):ee.children(t,se),s=n[0],r=i&&s&&s.classList.contains(n5),o=()=>this._transitionComplete(e,s,i);s&&r?(s.classList.remove(n7),this._queueCallback(o,e,!0)):o()}_transitionComplete(e,t,i){if(t){t.classList.remove(nJ);let n=ee.findOne(":scope > .dropdown-menu .active",t.parentNode);n&&n.classList.remove(nJ),"tab"===t.getAttribute("role")&&t.setAttribute("aria-selected",!1)}e.classList.add(nJ),"tab"===e.getAttribute("role")&&e.setAttribute("aria-selected",!0),g(e),e.classList.contains(n5)&&e.classList.add(n7);let s=e.parentNode;if(s&&"LI"===s.nodeName&&(s=s.parentNode),s&&s.classList.contains("dropdown-menu")){let r=e.closest(".dropdown");r&&ee.find(".dropdown-toggle",r).forEach(e=>e.classList.add(nJ)),e.setAttribute("aria-expanded",!0)}i&&i()}static jQueryInterface(e){return this.each(function(){let t=si.getOrCreateInstance(this);if("string"==typeof e){if(void 0===t[e])throw TypeError(`No method named "${e}"`);t[e]()}})}}j.on(document,nG,'[data-bs-toggle="tab"], [data-bs-toggle="pill"], [data-bs-toggle="list"]',function(e){if(["A","AREA"].includes(this.tagName)&&e.preventDefault(),u(this))return;let t=si.getOrCreateInstance(this);t.show()}),j.on(document,nZ,'[data-bs-toggle="tab-hover"]',function(e){if(["A","AREA"].includes(this.tagName)&&e.preventDefault(),u(this))return;let t=si.getOrCreateInstance(this);t.show()}),j.on(document,n6,'[data-bs-toggle="tab-drop"]',function(e){if(["A","AREA"].includes(this.tagName)&&e.preventDefault(),u(this))return;let t=si.getOrCreateInstance(this);Array.from(t._element.children).forEach(e=>{e.classList.contains(nJ)&&Array.from(e.children[0].children).forEach(e=>{e.children[0].classList.contains(nJ)&&Array.from(e.children).forEach(e=>{document.getElementById(e.getAttribute("data-bs-target").slice(1)).classList.remove(nJ),e.classList.remove(nJ)})})})}),E(si);let sn="toast",ss=".bs.toast",sr=`mouseover${ss}`,so=`mouseout${ss}`,sa=`focusin${ss}`,sl=`focusout${ss}`,sc=`hide${ss}`,sh=`hidden${ss}`,sd=`show${ss}`,su=`shown${ss}`,sf="hide",sp="show",sg="showing",sm={animation:"boolean",autohide:"boolean",delay:"number"},s8={animation:!0,autohide:!0,delay:5e3};class sv extends z{constructor(e,t){super(e),this._config=this._getConfig(t),this._timeout=null,this._hasMouseInteraction=!1,this._hasKeyboardInteraction=!1,this._setListeners()}static get DefaultType(){return sm}static get Default(){return s8}static get NAME(){return sn}show(){let e=j.trigger(this._element,sd);if(e.defaultPrevented)return;this._clearTimeout(),this._config.animation&&this._element.classList.add("fade");let t=()=>{this._element.classList.remove(sg),j.trigger(this._element,su),this._maybeScheduleHide()};this._element.classList.remove(sf),g(this._element),this._element.classList.add(sp),this._element.classList.add(sg),this._queueCallback(t,this._element,this._config.animation)}hide(){if(!this._element.classList.contains(sp))return;let e=j.trigger(this._element,sc);if(e.defaultPrevented)return;let t=()=>{this._element.classList.add(sf),this._element.classList.remove(sg),this._element.classList.remove(sp),j.trigger(this._element,sh)};this._element.classList.add(sg),this._queueCallback(t,this._element,this._config.animation)}dispose(){this._clearTimeout(),this._element.classList.contains(sp)&&this._element.classList.remove(sp),super.dispose()}_getConfig(e){return h(sn,e={...s8,...J.getDataAttributes(this._element),..."object"==typeof e&&e?e:{}},this.constructor.DefaultType),e}_maybeScheduleHide(){this._config.autohide&&!this._hasMouseInteraction&&!this._hasKeyboardInteraction&&(this._timeout=setTimeout(()=>{this.hide()},this._config.delay))}_onInteraction(e,t){switch(e.type){case"mouseover":case"mouseout":this._hasMouseInteraction=t;break;case"focusin":case"focusout":this._hasKeyboardInteraction=t}if(t){this._clearTimeout();return}let i=e.relatedTarget;!(this._element===i||this._element.contains(i))&&this._maybeScheduleHide()}_setListeners(){j.on(this._element,sr,e=>this._onInteraction(e,!0)),j.on(this._element,so,e=>this._onInteraction(e,!1)),j.on(this._element,sa,e=>this._onInteraction(e,!0)),j.on(this._element,sl,e=>this._onInteraction(e,!1))}_clearTimeout(){clearTimeout(this._timeout),this._timeout=null}static jQueryInterface(e){return this.each(function(){let t=sv.getOrCreateInstance(this,e);if("string"==typeof e){if(void 0===t[e])throw TypeError(`No method named "${e}"`);t[e](this)}})}}return q(sv),E(sv),{Alert:K,Button:Q,Carousel:eT,Collapse:ez,Dropdown:iA,Modal:i6,Offcanvas:nd,Popover:nP,ScrollSpy:nY,Tab:si,Toast:sv,Tooltip:nD}});